// :)
/**
 * synkro install — first-time setup on customer's machine.
 *
 * Detects installed AI agents (Claude Code, Cursor), drops hook scripts in
 * ~/.synkro/hooks/, writes settings.json hook entries, fetches the latest
 * Edit/Write prompt from the gateway and inlines it into settings, writes
 * config.env. Triggers `synkro login` if not already authed.
 */
import { existsSync, mkdirSync, writeFileSync, chmodSync, readFileSync, readdirSync, unlinkSync, statSync } from 'node:fs';
import { homedir } from 'node:os';
import { dirname, join } from 'node:path';
import { execSync, spawnSync } from 'node:child_process';
import { fileURLToPath } from 'node:url';
import { createInterface } from 'node:readline';
import { detectAgents, type DetectedAgent } from '../installer/agentDetect.js';
import { installCCHooks, uninstallCCHooks } from '../installer/ccHookConfig.js';
import { installCursorHooks, uninstallCursorHooks } from '../installer/cursorHookConfig.js';
import { installMcpConfig, installCursorMcpConfig, uninstallMcpConfig, uninstallCursorMcpConfig, uninstallClaudeDesktopMcpConfig } from '../installer/mcpConfig.js';
import { resolveSkillPaths } from '../installer/skillParser.js';
import { SYNKRO_COMMON_SCRIPT } from '../installer/hookScripts.js';
import { SYNKRO_COMMON_TS, EDIT_PRECHECK_TS, CWE_PRECHECK_TS, CVE_PRECHECK_TS, BASH_JUDGE_TS, AGENT_JUDGE_TS, PLAN_JUDGE_TS, STOP_SUMMARY_TS, SESSION_START_TS, BASH_FOLLOWUP_TS, TRANSCRIPT_SYNC_TS, USER_PROMPT_SUBMIT_TS, CURSOR_BASH_JUDGE_TS, CURSOR_EDIT_CAPTURE_TS, CURSOR_AGENT_CAPTURE_TS, INSTALL_SCAN_TS } from '../installer/hookScriptsTs.js';
import { STUB_COMMON_TS, STUB_EDIT_PRECHECK_TS, STUB_CWE_PRECHECK_TS, STUB_CVE_PRECHECK_TS, STUB_BASH_JUDGE_TS, STUB_BASH_FOLLOWUP_TS, STUB_AGENT_JUDGE_TS, STUB_PLAN_JUDGE_TS, STUB_STOP_SUMMARY_TS, STUB_SESSION_START_TS, STUB_TRANSCRIPT_SYNC_TS, STUB_USER_PROMPT_SUBMIT_TS, STUB_INSTALL_SCAN_TS, STUB_TASK_ACTIVATE_INTENT_TS, STUB_MCP_GATE_TS, STUB_CURSOR_BASH_JUDGE_TS, STUB_CURSOR_EDIT_CAPTURE_TS, STUB_CURSOR_AGENT_CAPTURE_TS } from '../installer/hookScriptsTs.js';
import { isAuthenticated, getAccessToken, authenticate, getUserInfo, ensureValidToken, refreshToken } from '../auth/stub.js';
import { promptRepoConnection } from './repoConnect.js';
import { setApiBaseUrl, listProjects } from '../api/projects.js';
// connectGitHub import removed — GitHub PR scanning is hidden; the install no
// longer connects GitHub. setupGithub.ts is kept for when it's re-enabled.
import { fetchJudgePrompts } from '../installer/promptFetcher.js';
import { claudeDesktopInstalled } from '../installer/claudeDesktopTap.js';
import { dockerInstall, waitForContainerReady, waitForWorkersReady, splitWorkers, resolveGraderPool, poolLabel, dockerStatus, dockerSafeStop, dockerRemove, type WorkerProvider } from '../local-cc/dockerInstall.js';
import { captureClaudeSetupToken } from '../local-cc/setupToken.js';

// Replaced by tsup `define` at build time.
declare const __SYNKRO_CLI_VERSION__: string;
declare const __INSTALL_EXTRACT_CORE_SRC__: string;

const SYNKRO_DIR = join(homedir(), '.synkro');
const HOOKS_DIR = join(SYNKRO_DIR, 'hooks');
const BIN_DIR = join(SYNKRO_DIR, 'bin');
const CONFIG_PATH = join(SYNKRO_DIR, 'config.env');

// Resolve the persisted hook mode (full vs stub). Reconcile paths (restart/
// update) don't re-parse CLI flags, so they read the choice the install
// recorded in config.env. Env var wins for one-off overrides. Stub is the
// DEFAULT; legacy full hooks are opt-in via --legacy. Only an explicit 'full'
// choice (flag or persisted SYNKRO_HOOK_MODE=full) keeps the old fat hooks —
// fresh installs and pre-stub installs with no persisted mode land on stub.
function resolvePersistedHookMode(): 'full' | 'stub' {
  if (process.env.SYNKRO_HOOK_MODE === 'full') return 'full';
  if (process.env.SYNKRO_HOOK_MODE === 'stub') return 'stub';
  try {
    const env = readFileSync(CONFIG_PATH, 'utf-8');
    if (/^SYNKRO_HOOK_MODE=['"]?full['"]?\s*$/m.test(env)) return 'full';
  } catch {}
  return 'stub';
}

const MCP_STDIO_PROXY_SRC = `#!/usr/bin/env bun
import { readFileSync } from 'node:fs';
import { homedir } from 'node:os';
import { join } from 'node:path';
import { createInterface } from 'node:readline';

const HOME = homedir();
const TOKEN_PATH = join(HOME, '.synkro', '.mcp-jwt');
const PORT = parseInt(process.env.SYNKRO_MCP_PORT || '18931', 10);
const URL = \`http://127.0.0.1:\${PORT}\`;

// Re-read the JWT on EVERY request, not once at spawn: a \`synkro restart\` rotates
// the token, and a proxy that cached the old one would 401 every call until it
// was respawned (a manual /mcp).
function loadToken() {
  try { return readFileSync(TOKEN_PATH, 'utf-8').trim(); } catch { return ''; }
}
function write(obj) { try { process.stdout.write(JSON.stringify(obj) + '\\n'); } catch {} }

// Answer \`initialize\` LOCALLY without touching the backend. Claude Code latches an
// MCP server as failed — forcing a manual /mcp — only when the child process exits
// or the initialize handshake fails. A \`synkro restart\` takes the backend down for
// ~30-60s, so if CC (re)connects in that window a forwarded initialize would fail
// and the server would be dead until /mcp. Replying here makes the handshake
// succeed unconditionally; tools/list and tools/call still hit the backend.
function localInitialize(msg) {
  return {
    jsonrpc: '2.0',
    id: msg.id,
    result: {
      protocolVersion: (msg && msg.params && msg.params.protocolVersion) || '2024-11-05',
      capabilities: { tools: {} },
      serverInfo: { name: 'synkro-guardrails-local', version: '1.0.0' },
    },
  };
}

// Cap a single JSON-RPC line. This proxy only carries small tool-call envelopes;
// refusing oversized input keeps a malicious or runaway client from buffering
// unbounded memory (CWE-400).
const MAX_LINE_BYTES = 4 * 1024 * 1024;

const rl = createInterface({ input: process.stdin, terminal: false });

rl.on('line', async (line) => {
  if (!line.trim()) return;
  if (line.length > MAX_LINE_BYTES) return;
  let msg;
  try { msg = JSON.parse(line); } catch { return; }
  // Notifications carry no id and need no reply.
  if (msg.id == null && typeof msg.method === 'string' && msg.method.startsWith('notifications/')) return;

  // initialize: try the backend briefly for the real instructions, but ALWAYS
  // fall back to a local reply so the handshake can never block or fail.
  if (msg.method === 'initialize') {
    try {
      const resp = await fetch(URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': \`Bearer \${loadToken()}\` },
        body: line,
        signal: AbortSignal.timeout(2500),
      });
      if (resp.ok) { process.stdout.write((await resp.text()) + '\\n'); return; }
    } catch {}
    write(localInitialize(msg));
    return;
  }

  // tool calls: forward, allowing a generous per-attempt window for slow calls
  // (create + decompose). If the backend is UNREACHABLE (mid-restart), retry only
  // briefly, then return a CLEAN JSON-RPC error and move on. Never hold the request
  // open for tens of seconds — a stdio request held that long wedges CC's MCP
  // client and every later call queues behind it. A returned error keeps the
  // client responsive and the server connected; the agent just retries.
  const REFUSED_BUDGET_MS = 4000;
  const deadline = Date.now() + REFUSED_BUDGET_MS;
  let attempt = 0;
  while (true) {
    try {
      const resp = await fetch(URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': \`Bearer \${loadToken()}\` },
        body: line,
        signal: AbortSignal.timeout(30000),
      });
      if (resp.status === 204) return;
      process.stdout.write((await resp.text()) + '\\n');
      return;
    } catch (err) {
      // A 30s timeout means the backend was UP but slow — don't spin. ECONNREFUSED /
      // fetch failed means it's unreachable → retry briefly within the budget.
      const name = err && err.name;
      const isTimeout = name === 'TimeoutError' || name === 'AbortError';
      if (!isTimeout && Date.now() < deadline) {
        attempt++;
        await new Promise((r) => setTimeout(r, Math.min(500, 150 * attempt)));
        continue;
      }
      if (msg.id != null) {
        write({
          jsonrpc: '2.0',
          id: msg.id,
          error: { code: -32603, message: 'Synkro MCP backend unavailable (container may be restarting) — retry in a moment' },
        });
      }
      return;
    }
  }
});
`;

interface InstallOptions {
  gatewayUrl?: string;       // override default
  apiKey?: string;            // skip prompting if provided (for tests/CI)
  skipAuth?: boolean;
  noMcp?: boolean;            // skip registering the guardrails MCP server
  force?: boolean;            // bypass the "already installed" short-circuit
  linkRepo?: boolean;         // auto-link current repo (non-interactive)
  cursorApiKey?: string;      // Cursor API key for the cursor grader pool
  hookMode?: 'full' | 'stub'; // 'stub' = thin pipe hooks; logic in the container
  byokGateway?: boolean;      // legacy: no-container BYOK grading via the gateway
}

// Accept a gateway URL only if it's a plain http(s) URL. The published CLI is
// invoked from arbitrary cwds where a developer's monorepo .env / op://
// reference / direnv export may have poisoned SYNKRO_GATEWAY_URL with a value
// the CLI can't actually call (e.g. "op://dev/synkro/gateway-url"). Silently
// ignoring those falls through to the prod default so customers never have to
// wrestle with shell hygiene before `synkro install` works.
function sanitizeGatewayCandidate(raw: string | undefined): string | undefined {
  if (!raw) return undefined;
  return /^https?:\/\//.test(raw) ? raw : undefined;
}

export function parseArgs(argv: string[]): InstallOptions {
  const opts: InstallOptions = {};
  for (const a of argv) {
    if (a.startsWith('--api-key=')) opts.apiKey = a.slice('--api-key='.length);
    else if (a.startsWith('--gateway=')) opts.gatewayUrl = a.slice('--gateway='.length);
    else if (a.startsWith('--cursor-api-key=')) opts.cursorApiKey = a.slice('--cursor-api-key='.length);
    else if (a === '--skip-auth') opts.skipAuth = true;
    else if (a === '--no-mcp') opts.noMcp = true;
    else if (a === '--force' || a === '-f') opts.force = true;
    else if (a === '--link-repo') opts.linkRepo = true;
    else if (a === '--byok-gateway') opts.byokGateway = true;
    else if (a === '--stub' || a === '--mode=stub') opts.hookMode = 'stub';
    // --legacy (alias --mode=full) opts into the old self-contained fat hooks.
    else if (a === '--legacy' || a === '--mode=full') opts.hookMode = 'full';
  }
  // space-separated forms: --mode stub | --mode full
  const modeIdx = argv.indexOf('--mode');
  if (modeIdx >= 0 && argv[modeIdx + 1] === 'stub') opts.hookMode = 'stub';
  if (modeIdx >= 0 && argv[modeIdx + 1] === 'full') opts.hookMode = 'full';
  // Env fallback so `synkro restart`/`update` (which don't re-parse flags)
  // keep an explicit mode across reconciliations.
  if (!opts.hookMode && process.env.SYNKRO_HOOK_MODE === 'full') opts.hookMode = 'full';
  if (!opts.hookMode && process.env.SYNKRO_HOOK_MODE === 'stub') opts.hookMode = 'stub';
  if (!opts.gatewayUrl) {
    const fromEnv = sanitizeGatewayCandidate(process.env.SYNKRO_GATEWAY_URL);
    if (fromEnv) opts.gatewayUrl = fromEnv;
  }
  // NOTE: we deliberately do NOT pick up SYNKRO_API_KEY from process.env. The
  // root .env / global env may contain a stale or unrelated SYNKRO_API_KEY.
  // The legitimate sources are: --api-key flag, or fresh OAuth via authenticate().
  return opts;
}

// Ask the user which detected agents they want guardrails installed for.
// If only one is detected, no prompt — return as-is. If multiple, show a
// picker that accepts: "1" (first), "2" (second), "3"/"both"/"all"/empty
// (everything). Anything else re-prompts.
async function promptAgentSelection<T extends { kind: string; name: string }>(
  detected: T[],
): Promise<T[]> {
  if (detected.length <= 1) return detected;

  console.log('Multiple coding agents detected. Which ones do you want Synkro guardrails installed for?');
  detected.forEach((a, i) => console.log(`  ${i + 1}. ${a.name}`));
  console.log(`  ${detected.length + 1}. Both / all (default)`);

  const rl = createInterface({ input: process.stdin, output: process.stdout });
  const ask = (): Promise<T[]> => new Promise((resolve) => {
    rl.question(`Pick [1-${detected.length + 1}] (default: all): `, (answer) => {
      const t = answer.trim().toLowerCase();
      if (t === '' || t === String(detected.length + 1) || t === 'both' || t === 'all') {
        rl.close();
        return resolve(detected);
      }
      const n = parseInt(t, 10);
      if (Number.isInteger(n) && n >= 1 && n <= detected.length) {
        rl.close();
        return resolve([detected[n - 1]]);
      }
      console.log('Invalid choice. Try again.');
      resolve(ask());
    });
  });
  return ask();
}

// Collect the Cursor API key when the install includes a Cursor worker pool.
// Sources, in order: --cursor-api-key flag, SYNKRO_CURSOR_API_KEY env, then
// an interactive paste prompt. An already-configured key is kept only if
// Cursor still accepts it.
async function promptCursorApiKey(opts: InstallOptions): Promise<void> {
  const { cursorApiKeyConfigured, writeCursorApiKey, validateCursorApiKey } = await import('../local-cc/macKeychain.js');
  if (cursorApiKeyConfigured()) {
    // A key file exists — confirm Cursor still accepts it before trusting it.
    // Only a definitive rejection (false) re-prompts; a network blip (null)
    // leaves the working key alone.
    const valid = await validateCursorApiKey();
    if (valid !== false) {
      console.log('  ✓ Cursor API key validated.');
      return;
    }
    console.warn('  ⚠ The saved Cursor API key was rejected by Cursor (revoked or expired).');
    console.warn('    Paste a fresh key below, or press Enter to skip (Cursor workers stay idle).');
    // fall through to re-source / re-prompt the key
  }

  const provided = (opts.cursorApiKey || process.env.SYNKRO_CURSOR_API_KEY || '').trim();
  if (provided) {
    writeCursorApiKey(provided);
    console.log('  ✓ Cursor API key saved to ~/.synkro/cursor-creds/api-key');
    return;
  }

  const rl = createInterface({ input: process.stdin, output: process.stdout });
  const key = await new Promise<string>((resolve) => {
    rl.question(
      'Cursor grading needs a Cursor API key (cursor.com → Settings → API Keys).\n' +
      'Paste it now, or press Enter to skip (Cursor workers stay idle until set): ',
      (answer) => { rl.close(); resolve(answer.trim()); },
    );
  });
  if (key) {
    writeCursorApiKey(key);
    console.log('  ✓ Cursor API key saved.');
  } else {
    console.log('  ⚠ Skipped — Cursor workers will be idle. Re-run install or pass --cursor-api-key=… later.');
  }
}

// ONE deployment choice: WHERE the grading worker container runs.
//   local — a container on THIS machine (Docker); storage = local PGLite.
//   cloud — a private PER-ORG container hosted by Synkro (Cloudflare Containers);
//           storage = Synkro Timescale; authed by the org's own setup-token.
// BYOK applies to both (local = your keychain Claude; cloud = your setup-token).
// Default — and the non-TTY answer — is local. The legacy no-container BYOK-
// gateway grading path is still reachable via `--byok-gateway` or an existing
// .synkro with `mode = "byok"`.
async function promptDeployLocation(): Promise<'local' | 'cloud'> {
  if (!process.stdin.isTTY) return 'local';
  const rl = createInterface({ input: process.stdin, output: process.stdout });
  return new Promise((resolve) => {
    rl.question(
      'Where should Synkro run?\n' +
      '  local — a grading container on this machine (Docker) (default)\n' +
      '  cloud — a private container hosted by Synkro (Cloudflare)\n' +
      'Both use your own Claude key. Choose [local] / cloud: ',
      (answer) => {
        rl.close();
        resolve(answer.trim().toLowerCase() === 'cloud' ? 'cloud' : 'local');
      },
    );
  });
}

// Harness-aware transcript-import consent. Offers to import the history of the
// agent(s) the user actually selected — not always Claude Code. With both
// selected, the user picks which source(s) to pull.
async function promptTranscriptSources(wantCC: boolean, wantCursor: boolean): Promise<{ cc: boolean; cursor: boolean }> {
  if (!wantCC && !wantCursor) return { cc: false, cursor: false };
  const rl = createInterface({ input: process.stdin, output: process.stdout });
  const ask = (q: string): Promise<string> =>
    new Promise((res) => rl.question(q, (a) => res(a.trim().toLowerCase())));
  try {
    if (wantCC && wantCursor) {
      console.log('Import and embed past session history? This indexes sessions so Ask Synkro');
      console.log('can answer questions about your patterns and the dashboard shows full history.');
      console.log('  1. Cursor only');
      console.log('  2. Claude Code only');
      console.log('  3. Both');
      console.log('  n. Skip');
      const a = await ask('Choose [1/2/3/n] (default: 3): ');
      if (a === 'n' || a === 'no') return { cc: false, cursor: false };
      if (a === '1') return { cc: false, cursor: true };
      if (a === '2') return { cc: true, cursor: false };
      return { cc: true, cursor: true }; // '', '3', or anything else → both
    }
    const which = wantCursor ? 'Cursor' : 'Claude Code';
    const a = await ask(
      `Import and embed your ${which} session history?\n` +
      'This indexes past sessions so Ask Synkro can answer questions about your\n' +
      'coding patterns and the dashboard shows full history. (Y/n) ',
    );
    const yes = a === '' || a === 'y' || a === 'yes';
    return { cc: wantCC && yes, cursor: wantCursor && yes };
  } finally {
    rl.close();
  }
}

const OFFSETS_DIR = join(SYNKRO_DIR, '.transcript-offsets');

function ensureSynkroDir(): void {
  mkdirSync(SYNKRO_DIR, { recursive: true });
  mkdirSync(HOOKS_DIR, { recursive: true });
  mkdirSync(BIN_DIR, { recursive: true });
  mkdirSync(OFFSETS_DIR, { recursive: true });
  mkdirSync(join(SYNKRO_DIR, 'sessions'), { recursive: true });
}

export function writeHookScripts(mode: 'full' | 'stub' = 'stub'): {
  bashScript: string;
  bashFollowupScript: string;
  editPrecheckScript: string;
  cwePrecheckScript: string;
  cvePrecheckScript: string;
  planJudgeScript: string;
  agentJudgeScript: string;
  stopSummaryScript: string;
  sessionStartScript: string;
  transcriptSyncScript: string;
  userPromptSubmitScript: string;
  installScanScript: string;
  cursorBashJudgeScript: string;
  cursorEditCaptureScript: string;
  cursorAgentCaptureScript: string;
  taskActivateIntentScript: string;
  mcpGateScript: string;
} {
  // Source is inlined at build time via tsup's `define` (see tsup.config.ts).
  // Reading it from disk doesn't work after npm publish because packages/api
  // isn't shipped with the CLI tarball.
  const installExtractCorePath = join(HOOKS_DIR, 'installExtractCore.ts');

  const bashScriptPath = join(HOOKS_DIR, 'cc-bash-judge.ts');
  const bashFollowupScriptPath = join(HOOKS_DIR, 'cc-bash-followup.ts');
  const editPrecheckScriptPath = join(HOOKS_DIR, 'cc-edit-precheck.ts');
  const cwePrecheckScriptPath = join(HOOKS_DIR, 'cc-cwe-precheck.ts');
  const cvePrecheckScriptPath = join(HOOKS_DIR, 'cc-cve-precheck.ts');
  const planJudgeScriptPath = join(HOOKS_DIR, 'cc-plan-judge.ts');
  const agentJudgeScriptPath = join(HOOKS_DIR, 'cc-agent-judge.ts');
  const stopSummaryScriptPath = join(HOOKS_DIR, 'cc-stop-summary.ts');
  const sessionStartScriptPath = join(HOOKS_DIR, 'cc-session-start.ts');
  const transcriptSyncScriptPath = join(HOOKS_DIR, 'cc-transcript-sync.ts');
  const userPromptSubmitScriptPath = join(HOOKS_DIR, 'cc-user-prompt-submit.ts');
  const commonScriptPath = join(HOOKS_DIR, '_synkro-common.ts');
  const commonBashScriptPath = join(HOOKS_DIR, '_synkro-common.sh');
  const installScanScriptPath = join(HOOKS_DIR, 'cc-install-scan.ts');
  const cursorBashJudgePath = join(HOOKS_DIR, 'cursor-bash-judge.ts');
  const cursorEditCapturePath = join(HOOKS_DIR, 'cursor-edit-capture.ts');
  const cursorAgentCapturePath = join(HOOKS_DIR, 'cursor-agent-capture.ts');
  const mcpStdioProxyPath = join(HOOKS_DIR, 'mcp-stdio-proxy.ts');
  const taskActivateIntentScriptPath = join(HOOKS_DIR, 'cc-task-activate-intent.ts');
  // MCP server governance gate. Always a thin forwarder (the gate logic lives in
  // the server's scanRouter), so it's the stub in both full and stub modes.
  const mcpGateScriptPath = join(HOOKS_DIR, 'cc-mcp-gate.ts');

  // ─── Stub mode: write ~3-line delegating stubs + the plumbing common ───
  // Same filenames as full mode (so the CC/Cursor hook registration is
  // unchanged), but every hook just pipes a fixed envelope to the local
  // server's /api/scan/{surface}. No scanning logic lands on disk — all the
  // brains live in the container, updatable via a GHCR push alone.
  if (mode === 'stub') {
    const stubCommonPath = join(HOOKS_DIR, '_synkro-stub-common.ts');
    const stubFiles: Array<[string, string]> = [
      [stubCommonPath, STUB_COMMON_TS],
      [bashScriptPath, STUB_BASH_JUDGE_TS],
      [bashFollowupScriptPath, STUB_BASH_FOLLOWUP_TS],
      [editPrecheckScriptPath, STUB_EDIT_PRECHECK_TS],
      [cwePrecheckScriptPath, STUB_CWE_PRECHECK_TS],
      [cvePrecheckScriptPath, STUB_CVE_PRECHECK_TS],
      [planJudgeScriptPath, STUB_PLAN_JUDGE_TS],
      [agentJudgeScriptPath, STUB_AGENT_JUDGE_TS],
      [stopSummaryScriptPath, STUB_STOP_SUMMARY_TS],
      [sessionStartScriptPath, STUB_SESSION_START_TS],
      [transcriptSyncScriptPath, STUB_TRANSCRIPT_SYNC_TS],
      [userPromptSubmitScriptPath, STUB_USER_PROMPT_SUBMIT_TS],
      [installScanScriptPath, STUB_INSTALL_SCAN_TS],
      [taskActivateIntentScriptPath, STUB_TASK_ACTIVATE_INTENT_TS],
      [mcpGateScriptPath, STUB_MCP_GATE_TS],
      [cursorBashJudgePath, STUB_CURSOR_BASH_JUDGE_TS],
      [cursorEditCapturePath, STUB_CURSOR_EDIT_CAPTURE_TS],
      [cursorAgentCapturePath, STUB_CURSOR_AGENT_CAPTURE_TS],
    ];
    for (const [p, content] of stubFiles) {
      writeFileSync(p, content, 'utf-8');
      chmodSync(p, 0o755);
    }
    // The MCP stdio proxy is the guardrails MCP server bridge, not hook logic —
    // needed in both modes for the `synkro-guardrails` tools.
    writeFileSync(mcpStdioProxyPath, MCP_STDIO_PROXY_SRC, 'utf-8');
    chmodSync(mcpStdioProxyPath, 0o755);
    // Remove full-mode-only artifacts so a stub install leaves a genuinely
    // minimal hooks dir — no dead 2,500-line common, no install-extract core.
    // (Stubs import only _synkro-stub-common.ts.)
    for (const stale of ['_synkro-common.ts', '_synkro-common.sh', 'installExtractCore.ts']) {
      try { unlinkSync(join(HOOKS_DIR, stale)); } catch {}
    }
    return {
      bashScript: bashScriptPath,
      bashFollowupScript: bashFollowupScriptPath,
      editPrecheckScript: editPrecheckScriptPath,
      cwePrecheckScript: cwePrecheckScriptPath,
      cvePrecheckScript: cvePrecheckScriptPath,
      planJudgeScript: planJudgeScriptPath,
      agentJudgeScript: agentJudgeScriptPath,
      stopSummaryScript: stopSummaryScriptPath,
      sessionStartScript: sessionStartScriptPath,
      transcriptSyncScript: transcriptSyncScriptPath,
      userPromptSubmitScript: userPromptSubmitScriptPath,
      installScanScript: installScanScriptPath,
      cursorBashJudgeScript: cursorBashJudgePath,
      cursorEditCaptureScript: cursorEditCapturePath,
      cursorAgentCaptureScript: cursorAgentCapturePath,
      taskActivateIntentScript: taskActivateIntentScriptPath,
      mcpGateScript: mcpGateScriptPath,
    };
  }

  writeFileSync(bashScriptPath, BASH_JUDGE_TS, 'utf-8');
  writeFileSync(bashFollowupScriptPath, BASH_FOLLOWUP_TS, 'utf-8');
  writeFileSync(editPrecheckScriptPath, EDIT_PRECHECK_TS, 'utf-8');
  writeFileSync(cwePrecheckScriptPath, CWE_PRECHECK_TS, 'utf-8');
  writeFileSync(cvePrecheckScriptPath, CVE_PRECHECK_TS, 'utf-8');
  writeFileSync(planJudgeScriptPath, PLAN_JUDGE_TS, 'utf-8');
  writeFileSync(agentJudgeScriptPath, AGENT_JUDGE_TS, 'utf-8');
  writeFileSync(stopSummaryScriptPath, STOP_SUMMARY_TS, 'utf-8');
  writeFileSync(sessionStartScriptPath, SESSION_START_TS, 'utf-8');
  writeFileSync(transcriptSyncScriptPath, TRANSCRIPT_SYNC_TS, 'utf-8');
  writeFileSync(userPromptSubmitScriptPath, USER_PROMPT_SUBMIT_TS, 'utf-8');
  writeFileSync(commonScriptPath, SYNKRO_COMMON_TS, 'utf-8');
  writeFileSync(commonBashScriptPath, SYNKRO_COMMON_SCRIPT, 'utf-8');
  writeFileSync(installScanScriptPath, INSTALL_SCAN_TS, 'utf-8');
  writeFileSync(cursorBashJudgePath, CURSOR_BASH_JUDGE_TS, 'utf-8');
  writeFileSync(cursorEditCapturePath, CURSOR_EDIT_CAPTURE_TS, 'utf-8');
  writeFileSync(cursorAgentCapturePath, CURSOR_AGENT_CAPTURE_TS, 'utf-8');
  writeFileSync(mcpStdioProxyPath, MCP_STDIO_PROXY_SRC, 'utf-8');
  writeFileSync(installExtractCorePath, __INSTALL_EXTRACT_CORE_SRC__, 'utf-8');
  writeFileSync(taskActivateIntentScriptPath, STUB_TASK_ACTIVATE_INTENT_TS, 'utf-8');
  // cc-mcp-gate is a thin forwarder in BOTH modes (the gate logic lives in the
  // server's scanRouter / cloudMcpGate), so it imports _synkro-stub-common.ts.
  // Full mode must therefore also write the stub-common, or the import dangles
  // ("Cannot find module './_synkro-stub-common.ts' from cc-mcp-gate.ts").
  const stubCommonPath = join(HOOKS_DIR, '_synkro-stub-common.ts');
  writeFileSync(stubCommonPath, STUB_COMMON_TS, 'utf-8');
  chmodSync(stubCommonPath, 0o755);
  writeFileSync(mcpGateScriptPath, STUB_MCP_GATE_TS, 'utf-8');

  chmodSync(bashScriptPath, 0o755);
  chmodSync(bashFollowupScriptPath, 0o755);
  chmodSync(editPrecheckScriptPath, 0o755);
  chmodSync(cwePrecheckScriptPath, 0o755);
  chmodSync(cvePrecheckScriptPath, 0o755);
  chmodSync(planJudgeScriptPath, 0o755);
  chmodSync(agentJudgeScriptPath, 0o755);
  chmodSync(stopSummaryScriptPath, 0o755);
  chmodSync(sessionStartScriptPath, 0o755);
  chmodSync(transcriptSyncScriptPath, 0o755);
  chmodSync(userPromptSubmitScriptPath, 0o755);
  chmodSync(commonScriptPath, 0o755);
  chmodSync(commonBashScriptPath, 0o755);
  chmodSync(installScanScriptPath, 0o755);
  chmodSync(cursorBashJudgePath, 0o755);
  chmodSync(cursorEditCapturePath, 0o755);
  chmodSync(cursorAgentCapturePath, 0o755);
  chmodSync(mcpStdioProxyPath, 0o755);
  chmodSync(installExtractCorePath, 0o755);
  chmodSync(taskActivateIntentScriptPath, 0o755);
  chmodSync(mcpGateScriptPath, 0o755);

  // NOTE: do NOT unlink _synkro-stub-common.ts here — full mode now writes it
  // (above) because cc-mcp-gate.ts forwards through runStub in both modes.
  // Deleting it dangles the mcp-gate import ("Cannot find module").

  return {
    bashScript: bashScriptPath,
    bashFollowupScript: bashFollowupScriptPath,
    editPrecheckScript: editPrecheckScriptPath,
    cwePrecheckScript: cwePrecheckScriptPath,
    cvePrecheckScript: cvePrecheckScriptPath,
    planJudgeScript: planJudgeScriptPath,
    agentJudgeScript: agentJudgeScriptPath,
    stopSummaryScript: stopSummaryScriptPath,
    sessionStartScript: sessionStartScriptPath,
    transcriptSyncScript: transcriptSyncScriptPath,
    userPromptSubmitScript: userPromptSubmitScriptPath,
    installScanScript: installScanScriptPath,
    cursorBashJudgeScript: cursorBashJudgePath,
    cursorEditCaptureScript: cursorEditCapturePath,
    cursorAgentCaptureScript: cursorAgentCapturePath,
    taskActivateIntentScript: taskActivateIntentScriptPath,
    mcpGateScript: mcpGateScriptPath,
  };
}

// Sanitize values before writing into config.env — the file is sourced as a
// shell script, so any unquoted shell metacharacter in a value is dangerous
// (newlines smuggle new assignments; (){}#`$ etc. let an attacker run code
// on `source config.env`). Two defenses:
//   1. Strip non-printable ASCII (drops newlines, tabs, control chars)
//   2. Wrap the result in single-quotes — single-quoted shell strings are
//      entirely literal, so anything inside is safe regardless of contents.
//      Internal single-quotes are escaped using the standard '\'' trick.
function sanitizeConfigValue(raw: string | undefined, maxLen = 256): string {
  if (!raw) return '';
  return raw
    .replace(/[^\x20-\x7E]/g, '')   // drop non-printable (newlines/tabs/control)
    .slice(0, maxLen);
}

function shellQuoteSingle(value: string): string {
  // Escape any single quote in `value` for inclusion inside a single-quoted
  // shell string: 'foo'"'"'bar' renders as foo'bar.
  return `'${value.replace(/'/g, "'\\''")}'`;
}

// Resolve the absolute path to the synkro bundle (dist/bootstrap.js) so hook
// scripts can invoke `node "$SYNKRO_CLI_BIN" grade ...` without relying on
// the user's shell PATH. Returning a single path (vs a command string) keeps
// the hook invocation a single quoted arg — no shell-injection surface.
//
// process.argv[1] is the script the user executed: with the pnpm/npm shim
// it's the absolute path to dist/bootstrap.js. With `just synkro install`
// it's also the absolute bundle path (the just recipe runs `node bootstrap.js`).
function resolveSynkroBundle(): string | null {
  const scriptPath = process.argv[1];
  if (scriptPath && existsSync(scriptPath)) return scriptPath;
  return null;
}

function writeConfigEnv(opts: { gatewayUrl: string; userId?: string; orgId?: string; email?: string; tier?: string; inference?: string; synkroBin?: string | null; transcriptConsent?: boolean; localInference?: boolean; deploymentMode?: 'bare-host' | 'docker'; gradingMode?: 'local' | 'byok'; storageMode?: 'local' | 'cloud'; deployLocation?: 'local' | 'cloud'; hookMode?: 'full' | 'stub' }): void {
  const credsPath = join(SYNKRO_DIR, 'credentials.json');
  const safeGateway = sanitizeConfigValue(opts.gatewayUrl);
  const safeUserId = sanitizeConfigValue(opts.userId);
  const safeOrgId = sanitizeConfigValue(opts.orgId);
  const safeEmail = sanitizeConfigValue(opts.email);
  const safeTier = sanitizeConfigValue(opts.tier ?? 'pro', 32);
  const safeInference = sanitizeConfigValue(opts.inference ?? 'fast', 16);
  // Allow up to 1KB for the bin command since it may be `node /long/path/to/bootstrap.js`.
  const safeSynkroBin = sanitizeConfigValue(opts.synkroBin ?? '', 1024);
  const lines = [
    '# Synkro CLI config (managed by synkro install)',
    '# JWT auth — the hook scripts read SYNKRO_CREDENTIALS_PATH at runtime',
    '# and send Authorization: Bearer <access_token> on every gateway call.',
    `SYNKRO_GATEWAY_URL=${shellQuoteSingle(safeGateway)}`,
    `SYNKRO_CREDENTIALS_PATH=${shellQuoteSingle(credsPath)}`,
    `SYNKRO_TIER=${shellQuoteSingle(safeTier)}`,
    `SYNKRO_INFERENCE=${shellQuoteSingle(safeInference)}`,
    `SYNKRO_VERSION=${shellQuoteSingle(__SYNKRO_CLI_VERSION__)}`,
  ];
  if (safeSynkroBin) lines.push(`SYNKRO_CLI_BIN=${shellQuoteSingle(safeSynkroBin)}`);
  if (safeUserId) lines.push(`SYNKRO_USER_ID=${shellQuoteSingle(safeUserId)}`);
  if (safeOrgId) lines.push(`SYNKRO_ORG_ID=${shellQuoteSingle(safeOrgId)}`);
  if (safeEmail) lines.push(`SYNKRO_EMAIL=${shellQuoteSingle(safeEmail)}`);
  if (opts.transcriptConsent !== undefined) {
    lines.push(`SYNKRO_TRANSCRIPT_CONSENT=${shellQuoteSingle(opts.transcriptConsent ? 'yes' : 'no')}`);
  }
  lines.push(`SYNKRO_LOCAL_INFERENCE=${shellQuoteSingle(opts.localInference ? 'yes' : 'no')}`);
  // Persisted so hooks see the same value without shell-sourced env.
  const safeMode = sanitizeConfigValue(opts.deploymentMode ?? 'docker', 16);
  lines.push(`SYNKRO_DEPLOYMENT_MODE=${shellQuoteSingle(safeMode)}`);
  // User-owned data axes — where grading runs and where telemetry is stored.
  lines.push(`SYNKRO_GRADING_MODE=${shellQuoteSingle(sanitizeConfigValue(opts.gradingMode ?? 'local', 16))}`);
  lines.push(`SYNKRO_STORAGE_MODE=${shellQuoteSingle(sanitizeConfigValue(opts.storageMode ?? 'local', 16))}`);
  // Canonical deployment location — where the grading container runs. The two
  // legacy mode fields above are derived from this for back-compat readers.
  lines.push(`SYNKRO_DEPLOY_LOCATION=${shellQuoteSingle(sanitizeConfigValue(opts.deployLocation ?? 'local', 16))}`);
  // Hook delivery mode — 'stub' = thin pipe hooks (scanning logic lives in the
  // container, the DEFAULT); 'full' = legacy self-contained hooks (--legacy).
  // Read by reconcile paths.
  lines.push(`SYNKRO_HOOK_MODE=${shellQuoteSingle(sanitizeConfigValue(opts.hookMode ?? 'stub', 8))}`);
  lines.push('');
  writeFileSync(CONFIG_PATH, lines.join('\n'), 'utf-8');
  chmodSync(CONFIG_PATH, 0o600);
}

/**
 * Cloud install: capture the org's Claude setup-token (CLOUD ONLY) and POST it
 * to the gateway, which stores it as the org secret and provisions the per-org
 * Cloudflare Containers grader. The token lives only in the request body —
 * never logged, never written to config/.synkro. Local installs never call this.
 */
const CLOUD_JWT_PATH = join(SYNKRO_DIR, '.cloud-jwt');

/** True if a JWT is expired (or unparseable), with a 5-minute safety buffer. */
function jwtExpired(token: string): boolean {
  try {
    const p = JSON.parse(Buffer.from(token.split('.')[1], 'base64url').toString('utf-8')) as { exp?: number };
    if (!p?.exp) return true;
    return Date.now() > p.exp * 1000 - 5 * 60 * 1000;
  } catch { return true; }
}

/**
 * Long-lived (1-year) cloud token for cloud CLI→gateway calls (provision /
 * local↔cloud switch). On first use it mints one via /api/v1/cli/cloud-token
 * (which needs a fresh WorkOS JWT) and stores it in ~/.synkro/.cloud-jwt; on
 * every later call it reuses the stored token while still valid and only
 * re-mints when expired. This is why switching deploy location no longer 401s on
 * the 5-minute WorkOS access token — exactly the failure that bit us re-provisioning.
 */
export async function getOrMintCloudToken(gatewayUrl: string): Promise<string> {
  // CWE-918: never fetch a caller-supplied gateway URL without the allowlist check.
  assertGatewayAllowed(gatewayUrl);

  let stored = '';
  try { stored = readFileSync(CLOUD_JWT_PATH, 'utf-8').trim(); } catch { /* none yet */ }
  if (stored && !jwtExpired(stored)) return stored;

  // Expired/missing → mint a fresh one. Minting requires a real user, so
  // force-refresh the WorkOS token first (the CLI's own expiry check has
  // disagreed with the gateway before, so don't trust isTokenExpired here).
  await refreshToken().catch(() => false);
  const jwt = getAccessToken();
  if (!jwt) throw new Error('Not authenticated — run `synkro login`');
  const resp = await fetch(`${gatewayUrl}/api/v1/cli/cloud-token`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${jwt}`, 'Content-Type': 'application/json' },
  });
  if (!resp.ok) {
    const t = await resp.text().catch(() => '');
    throw new Error(`cloud-token mint failed (${resp.status}): ${t.slice(0, 200)}`);
  }
  const { token } = await resp.json() as { token: string };
  writeFileSync(CLOUD_JWT_PATH, token + '\n', { mode: 0o600 });
  return token;
}

async function provisionCloudContainer(opts: {
  gatewayUrl: string; jwt: string;
  orgId?: string; userId?: string; email?: string;
  hasClaudeCode: boolean; hasCursor: boolean;
}): Promise<void> {
  let setupToken: string;
  try {
    console.log('  Authorize your Claude key for the hosted Synkro worker —');
    console.log('  a browser window will open for approval...\n');
    setupToken = await captureClaudeSetupToken();
  } catch (err) {
    console.error(`  ✗ Could not capture Claude setup-token: ${(err as Error).message}`);
    console.error('    Cloud needs a Claude setup-token. Run `claude setup-token` manually, then re-run install.');
    process.exit(1);
  }

  // Validate the captured token by ACTUALLY USING IT — the same guard the GitHub
  // path runs (setupGithub.ts). A truncated/wrong-account/no-credits token sails
  // through capture but 401s the moment Claude calls the API; round-tripping it
  // through a real `claude --print` here catches that at the user's machine,
  // BEFORE it's stored in Vault and only fails (silently) inside the container.
  // The container authenticates exactly this way (CLAUDE_CODE_OAUTH_TOKEN), so a
  // pass here means a pass there.
  console.log('  Validating Claude token...');
  try {
    const out = execSync('claude --print --output-format json "say ok"', {
      env: { ...process.env, CLAUDE_CODE_OAUTH_TOKEN: setupToken },
      encoding: 'utf-8',
      timeout: 30_000,
      stdio: ['ignore', 'pipe', 'pipe'],
    });
    const result = JSON.parse(out) as { is_error?: boolean; result?: string };
    if (result.is_error) throw new Error(result.result || 'auth failed');
    console.log('  ✓ Claude token validated.\n');
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    console.error(`  ✗ Claude token failed validation: ${msg}`);
    console.error('    The captured token can\'t authenticate (truncated capture, wrong account, or no API credits).');
    console.error('    Re-run install to re-authorize — nothing was stored.');
    process.exit(1);
  }

  const repo = detectGitRepo() || undefined;
  const sf = readFullSynkroFile();
  const harness: string[] = [];
  if (opts.hasClaudeCode) harness.push('claude-code');
  if (opts.hasCursor) harness.push('cursor');
  // Honor the worker count from synkro.toml ([grader] claude-code = N); fall back
  // to the env override, then a sane cloud default. This is what the org's
  // container boots — clamped server-side to whatever the instance can hold.
  const envWorkers = parseInt(process.env.SYNKRO_WORKERS_PER_POOL || '', 10);
  const total = (sf?.workers?.claude && sf.workers.claude > 0)
    ? sf.workers.claude
    : (Number.isFinite(envWorkers) && envWorkers > 0 ? envWorkers : 4);
  // Cursor pool size from synkro.toml ([grader] cursor = N); 0 if not configured.
  const cursorTotal = (sf?.workers?.cursor && sf.workers.cursor > 0) ? sf.workers.cursor : 0;
  // When a cursor pool is configured, ship the org's Cursor API key (written to
  // ~/.synkro/cursor-creds/api-key by promptCursorApiKey) so cloud cursor workers
  // can grade. Only in the body — never logged or written to .synkro config.
  let cursorApiKey = '';
  if (cursorTotal > 0) {
    try { cursorApiKey = readFileSync(join(SYNKRO_DIR, 'cursor-creds', 'api-key'), 'utf-8').trim(); } catch {}
  }
  // Use the long-lived cloud token so this — and especially a later local↔cloud
  // switch — never 401s on an expired 5-min WorkOS token. Falls back to the
  // session JWT if the cloud token can't be obtained.
  let cloudJwt = opts.jwt;
  try { cloudJwt = await getOrMintCloudToken(opts.gatewayUrl); }
  catch (e) { console.warn(`  (cloud token unavailable, using session token: ${(e as Error).message})`); }

  try {
    const resp = await fetch(`${opts.gatewayUrl}/api/v1/cli/cloud-provision`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${cloudJwt}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        org_id: opts.orgId,
        user_id: opts.userId,
        email: opts.email,
        harness,
        claude_workers: total,
        cursor_workers: cursorTotal,
        cursor_api_key: cursorApiKey, // never logged; gateway stores it as the org secret
        connected_repo: repo,
        ruleset: readFullSynkroFile()?.ruleset || 'default',
        setup_token: setupToken, // never logged; gateway stores it as the org secret
      }),
    });
    if (!resp.ok) {
      const t = await resp.text().catch(() => '');
      throw new Error(`cloud-provision failed (${resp.status}): ${t.slice(0, 200)}`);
    }
    const out = await resp.json().catch(() => ({})) as { status?: string; container_id?: string; expires_at?: string };
    console.log(`  ✓ cloud container provisioned${out.container_id ? ` (${out.container_id})` : ''}`);
    console.log();
  } catch (err) {
    console.error(`  ✗ Cloud provisioning failed: ${(err as Error).message}`);
    console.error('    Hooks + MCP are installed; re-run `synkro install` → cloud to retry.');
    console.log();
  } finally {
    setupToken = ''; // clear from memory
  }
}

/**
 * On a failed smoke grade, fetch the container's per-worker log tails (the actual
 * claude error — auth, crash, etc.) and print them. This is the only way to see
 * the container-internal failure (CF doesn't surface container stdout).
 */
async function printWorkerDebug(base: string, jwt: string): Promise<void> {
  try {
    console.warn('  ── container worker logs (the actual claude error) ──');
    const r = await fetch(`${base}/debug`, { headers: { Authorization: `Bearer ${jwt}` }, signal: AbortSignal.timeout(90_000) });
    const d = (await r.json().catch(() => null)) as
      | { grader_error_code?: string | null; grader_error_detail?: string | null; workers?: Array<{ worker: string; logTail: string; channelLog?: string; pane?: string; errorCode?: string }>; sick_pool?: Array<{ kind: string; sickReason: string }> }
      | null;
    if (!d || !Array.isArray(d.workers)) { console.warn(`    (worker debug unavailable: HTTP ${r.status})\n`); return; }
    // Lead with the classified cause if the container flagged one — no need to
    // read the raw screen below.
    if (d.grader_error_code) {
      console.warn(`    ▸ DIAGNOSIS [${d.grader_error_code}]: ${d.grader_error_detail || ''}`);
    }
    // Show one worker in full (channel flow is the same across them).
    for (const w of d.workers.slice(0, 2)) {
      console.warn(`    [${w.worker}] launcher log:`);
      for (const l of String(w.logTail || '').split('\n').filter((l) => l.trim()).slice(-6)) console.warn('      ' + l.slice(0, 240));
      if (w.channelLog && !w.channelLog.startsWith('(no channel')) {
        console.warn(`    [${w.worker}] channel flow (token + grade delivery):`);
        for (const l of String(w.channelLog).split('\n').filter((l) => l.trim()).slice(-12)) console.warn('      ' + l.slice(0, 240));
      } else {
        console.warn(`    [${w.worker}] channel: ${w.channelLog}`);
      }
      if (w.pane && !w.pane.startsWith('(') ) {
        console.warn(`    [${w.worker}] claude screen:`);
        for (const l of String(w.pane).split('\n').filter((l) => l.trim()).slice(-12)) console.warn('      ' + l.slice(0, 240));
      }
    }
    if (Array.isArray(d.sick_pool) && d.sick_pool.length) {
      console.warn('    sick workers:');
      for (const s of d.sick_pool) console.warn(`      ${s.kind}: ${s.sickReason || '(no reason)'}`);
    }
    console.warn('');
  } catch { console.warn('    (could not fetch worker debug)\n'); }
}

/**
 * Warm + verify the cloud grader after provisioning: boot the org's container,
 * wait for a healthy Claude worker, then run ONE smoke grade and assert a real
 * <synkro-verdict>. Surfaces a broken grader at install time instead of silently
 * declaring success. Returns true only when grading actually works end-to-end.
 */
async function verifyCloudGrader(jwt: string): Promise<boolean> {
  const base = (process.env.SYNKRO_CONTAINERS_URL || 'https://api.synkro.sh').replace(/\/$/, '');
  console.log('  Warming the cloud grader (booting container + checking workers)...');

  // 1. Boot + wait for a healthy worker via /diag (the container cold-starts here).
  const deadline = Date.now() + 180_000;
  let healthy = 0;
  while (Date.now() < deadline) {
    try {
      const r = await fetch(`${base}/diag`, { headers: { Authorization: `Bearer ${jwt}` }, signal: AbortSignal.timeout(60_000) });
      if (r.ok) {
        const j = (await r.json()) as { claude_code?: { healthy?: number } };
        healthy = j.claude_code?.healthy ?? 0;
        if (healthy >= 1) break;
      }
    } catch { /* still cold-booting */ }
    await new Promise((res) => setTimeout(res, 5000));
  }
  if (healthy < 1) {
    console.warn('  ⚠ cloud grader did not report a healthy worker within ~3m.');
    console.warn('    Grading is NOT verified — check the container or re-run `synkro install`.\n');
    return false;
  }
  console.log(`  ✓ ${healthy} Claude worker(s) healthy`);

  // 2. Smoke grade — a known-risky edit must come back as a real verdict.
  // The grade can take a few seconds (and up to 120s on a cold worker), so tick
  // an elapsed-time line while we wait — otherwise it just looks frozen.
  console.log('  Running smoke grade (a worker scores a known-risky edit; up to 120s)...');
  const started = Date.now();
  const ticker = setInterval(() => {
    const secs = Math.round((Date.now() - started) / 1000);
    process.stdout.write(`\r    …grading (${secs}s elapsed)   `);
  }, 1000);
  const stopTicker = () => { clearInterval(ticker); process.stdout.write('\r\x1b[2K'); };
  try {
    const probe = 'Grade this edit. File: t.py. Change: os.system(request.args["cmd"])';
    const r = await fetch(`${base}/grade/submit`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${jwt}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ role: 'grade-edit', payload: probe, content: probe, agent_kind: 'claude_code' }),
      signal: AbortSignal.timeout(120_000),
    });
    stopTicker();
    const raw = await r.text();
    let body: { result?: string; error?: string; error_code?: string; hint?: string } = {};
    try { body = JSON.parse(raw); } catch { /* non-JSON error page */ }
    if (r.ok && typeof body.result === 'string' && body.result.includes('<synkro-verdict>')) {
      console.log('  ✓ smoke grade passed — cloud grading is live\n');
      return true;
    }
    // Surface the ACTUAL container error instead of guessing — these distinguish
    // the failure modes (token vs no-worker vs container drop).
    const detail = (body.error || raw || '').trim().slice(0, 300) || '(empty response)';
    console.warn(`  ✗ smoke grade FAILED (HTTP ${r.status}): ${detail}`);
    if (body.error_code) {
      // The container classified the failure — show the code + actionable hint and
      // skip the heuristic guessing below.
      console.warn(`    ▸ ${body.error_code}: ${body.hint || ''}`);
    } else if (/no grader worker ready/i.test(detail)) {
      console.warn('    → the worker pool never registered (cold-start, or the pool failed to boot).');
    } else if (/connection lost|network|502/i.test(detail)) {
      console.warn('    → the container dropped the connection mid-grade — the claude worker crashed or hung (most often a bad/expired setup-token).');
    } else {
      console.warn('    → the worker returned an error; most often a bad/expired Claude setup-token.');
    }
    if (!body.error_code) console.warn('    Re-run `synkro install` → cloud to re-authorize the hosted worker.\n');
    await printWorkerDebug(base, jwt);
    return false;
  } catch (e) {
    stopTicker();
    const reason = (e as Error)?.name === 'TimeoutError'
      ? 'the grade never returned within 120s (worker hung — usually claude auth)'
      : `the request failed (${(e as Error)?.message || 'network'})`;
    console.warn(`  ✗ smoke grade FAILED: ${reason}.`);
    console.warn('    The container is provisioned but a grade does not complete; re-run `synkro install` → cloud.\n');
    await printWorkerDebug(base, jwt);
    return false;
  }
}

/**
 * Read the deploy location the hooks currently route on, from config.env.
 * Defaults to 'local' when unset (the field is only ever written by
 * install/restart).
 */
function readPersistedDeployLocation(): 'local' | 'cloud' {
  try {
    if (existsSync(CONFIG_PATH)) {
      const m = readFileSync(CONFIG_PATH, 'utf-8').match(/^SYNKRO_DEPLOY_LOCATION='([^']*)'/m);
      if (m?.[1] === 'cloud') return 'cloud';
    }
  } catch { /* default local */ }
  return 'local';
}

/**
 * Surgically repoint the location keys in config.env, preserving every other
 * line. The hooks read SYNKRO_DEPLOY_LOCATION at call time, so this is what
 * actually reroutes grading between the local container and the CF container.
 */
function updateConfigEnvLocation(location: 'local' | 'cloud'): void {
  if (!existsSync(CONFIG_PATH)) return;
  let env = readFileSync(CONFIG_PATH, 'utf-8');
  const set = (k: string, v: string) => {
    const re = new RegExp(`^${k}=.*$`, 'm');
    const line = `${k}='${v}'`;
    env = re.test(env) ? env.replace(re, line) : `${env.replace(/\n?$/, '\n')}${line}`;
  };
  set('SYNKRO_DEPLOY_LOCATION', location);
  set('SYNKRO_STORAGE_MODE', location === 'cloud' ? 'cloud' : 'local');
  writeFileSync(CONFIG_PATH, env, 'utf-8');
  chmodSync(CONFIG_PATH, 0o600);
}

/**
 * Reconcile the deploy location declared in synkro.toml ([grader] location)
 * against what's persisted in config.env, switching local<->cloud when it
 * changed. `synkro restart` calls this so a user can flip the toml and have it
 * picked up without a full reinstall.
 *
 * Returns the resolved location. When it returns 'cloud' the switch is COMPLETE
 * (local container torn down, CF container provisioned, config.env repointed) and
 * the caller must SKIP its local docker steps. When 'local', config.env is local
 * and the caller runs its normal docker (re)start.
 */
/**
 * (Re)register the guardrails MCP for a deploy location: local → stdio proxy to
 * the local container; cloud → the hosted HTTP endpoint
 * (${gatewayUrl}/api/v1/mcp/guardrails, served by the api worker off Timescale),
 * so the rules/standards server follows the grader on a local<->cloud switch.
 *
 * The MCP JWT is the same long-lived org-scoped mcp:guardrails token for both
 * locations, so we REUSE the one minted at install (read-only — ~/.synkro is
 * never written here). If it's absent we mint one in-memory for the editor
 * config; persisting it is `synkro install`'s job, not this switch's.
 */
async function applyMcpConfig(opts: {
  gatewayUrl: string; jwt: string; hasClaudeCode: boolean; hasCursor: boolean; local: boolean;
}): Promise<void> {
  if (!opts.hasClaudeCode && !opts.hasCursor) return;
  let mcpJwt = '';
  try { mcpJwt = readFileSync(join(SYNKRO_DIR, '.mcp-jwt'), 'utf-8').trim(); } catch { /* mint below */ }
  if (!mcpJwt) {
    // Never log the token or raw fetch errors next to it (CWE-200).
    try {
      const r = await fetch(`${opts.gatewayUrl}/api/v1/cli/mcp-token`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${opts.jwt}`, 'Content-Type': 'application/json' },
        body: '{}',
      });
      if (r.ok) mcpJwt = ((await r.json()) as { token: string }).token;
      else console.warn(`  ⚠ MCP token mint failed (${r.status}) — run \`synkro install\` to set MCP up.`);
    } catch { console.warn('  ⚠ MCP token mint failed (network) — run `synkro install` to set MCP up.'); }
  }
  if (opts.hasClaudeCode) {
    try {
      const mcp = installMcpConfig({ gatewayUrl: opts.gatewayUrl, bearerToken: mcpJwt, local: opts.local });
      console.log(`  ✓ Claude Code MCP → ${mcp.url}`);
    } catch (err) { console.warn(`  ⚠ Claude Code MCP config failed: ${(err as Error).message}`); }
  }
  if (opts.hasCursor) {
    try {
      const mcp = installCursorMcpConfig({ gatewayUrl: opts.gatewayUrl, bearerToken: opts.local ? '' : mcpJwt, local: opts.local });
      console.log(`  ✓ Cursor MCP → ${mcp.url}`);
    } catch (err) { console.warn(`  ⚠ Cursor MCP config failed: ${(err as Error).message}`); }
  }
}

export async function reconcileDeployLocation(): Promise<{ location: 'local' | 'cloud'; changed: boolean }> {
  const sf = readFullSynkroFile();
  const desired: 'local' | 'cloud' = sf?.grader.location === 'cloud' ? 'cloud' : 'local';
  const current = readPersistedDeployLocation();
  if (desired === current) return { location: desired, changed: false };
  const hasClaudeCode = sf ? sf.harness.includes('claude-code') : true;
  const hasCursor = sf ? sf.harness.includes('cursor') : false;

  console.log(`\nsynkro.toml: deploy location changed ${current} → ${desired}\n`);

  // Both directions repoint the MCP (rules/standards) server → resolve auth +
  // gateway once, up front.
  if (!isAuthenticated()) {
    console.log('  Opening browser for Synkro auth...');
    const ok = await authenticate(() => {});
    if (!ok) { console.error('  ✗ authentication failed'); process.exit(1); }
  }
  await ensureValidToken();
  const token = getAccessToken();
  if (!token) { console.error('  ✗ no access token — run `synkro login`'); process.exit(1); }
  const gatewayUrl = sanitizeGatewayCandidate(process.env.SYNKRO_GATEWAY_URL) || 'https://api.synkro.sh';
  // CWE-918: reject a hostile SYNKRO_GATEWAY_URL override before we send the JWT
  // (+ setup-token) to it — same allowlist the install path enforces.
  try { assertGatewayAllowed(gatewayUrl); }
  catch (err) { console.error((err as Error).message); process.exit(1); }

  if (desired === 'cloud') {
    // local → cloud: gracefully tear down the local grader, provision the CF
    // container, then repoint grading + rules/MCP at the cloud.
    if (dockerStatus().running) {
      console.log('  Stopping local grader container (snapshotting state first)...');
      await dockerSafeStop();
    }
    dockerRemove(); // harmless if already gone
    console.log('  ✓ local container stopped + removed');

    let orgId: string | undefined, userId: string | undefined, email: string | undefined;
    try { const info = getUserInfo(); userId = info.id; orgId = info.org_id; email = info.email; } catch { /* unreachable post-auth */ }
    await provisionCloudContainer({ gatewayUrl, jwt: token, orgId, userId, email, hasClaudeCode, hasCursor });
    updateConfigEnvLocation('cloud');
    await applyMcpConfig({ gatewayUrl, jwt: token, hasClaudeCode, hasCursor, local: false });
    await verifyCloudGrader(token);
    console.log('  ✓ grading + rules/MCP now served from Synkro Cloud\n');
  } else {
    // cloud → local: repoint grading + MCP to local now; the caller's docker
    // (re)start brings the local grader container (which serves the MCP) up.
    updateConfigEnvLocation('local');
    await applyMcpConfig({ gatewayUrl, jwt: token, hasClaudeCode, hasCursor, local: true });
    console.log('  ✓ switched to local — bringing the local grader + MCP up\n');
  }
  return { location: desired, changed: true };
}

/**
 * Recycle the org's cloud grader container (destroy → fresh boot), then warm +
 * verify it. Called by `synkro restart` when already on cloud, so a restart spins
 * a NET-NEW container (picks up a rotated token / new image, clears wedged state).
 */
export async function recycleCloudContainer(): Promise<void> {
  if (!isAuthenticated()) { console.error('Not authenticated — run `synkro login`.'); return; }
  await ensureValidToken();
  const token = getAccessToken();
  if (!token) { console.error('No access token — run `synkro login`.'); return; }
  const base = (process.env.SYNKRO_CONTAINERS_URL || 'https://api.synkro.sh').replace(/\/$/, '');
  console.log('Synkro: recycling the cloud grader container (destroy → fresh boot)…');
  try {
    const r = await fetch(`${base}/recycle`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, signal: AbortSignal.timeout(30_000) });
    if (r.ok) console.log('  ✓ old container destroyed');
    else console.warn(`  ⚠ recycle returned HTTP ${r.status} — continuing to warm anyway.`);
  } catch { console.warn('  ⚠ recycle request failed (network) — continuing to warm anyway.'); }
  // Boot the fresh container + confirm it actually grades.
  await verifyCloudGrader(token);
}

/**
 * Resolve the deployment mode. Live env var wins (lets ops experiment without
 * mutating the persisted config), then the persisted SYNKRO_DEPLOYMENT_MODE
 * from ~/.synkro/config.env, then defaults to docker.
 *
 * Returns the lowercased string for direct comparison.
 */
function resolveDeploymentMode(): 'docker' | 'bare-host' {
  const envOverride = process.env.SYNKRO_DEPLOYMENT_MODE?.toLowerCase();
  if (envOverride === 'bare-host' || envOverride === 'docker') return envOverride;
  try {
    if (existsSync(CONFIG_PATH)) {
      const m = readFileSync(CONFIG_PATH, 'utf-8').match(/^SYNKRO_DEPLOYMENT_MODE='([^']*)'/m);
      const val = m?.[1]?.toLowerCase();
      if (val === 'bare-host' || val === 'docker') return val;
    }
  } catch { /* fall through to default */ }
  return 'docker';
}

function collectLocalMetadata(includeClaudeCode = true): Record<string, unknown> {
  const meta: Record<string, unknown> = { platform: process.platform };
  try {
    meta.display_name = execSync('git config user.name', { encoding: 'utf-8', timeout: 3000 }).trim();
  } catch {}
  try {
    const remote = execSync('git remote get-url origin', { encoding: 'utf-8', timeout: 3000, stdio: ['pipe', 'pipe', 'pipe'] }).trim();
    const sshMatch = remote.match(/^git@[^:]+:(.+?)(?:\.git)?$/);
    const httpMatch = remote.match(/^https?:\/\/[^/]+\/(.+?)(?:\.git)?$/);
    const m = sshMatch || httpMatch;
    if (m) meta.active_repo = m[1];
  } catch {}

  if (!includeClaudeCode) return meta;

  try {
    meta.cc_version = execSync('claude --version', { encoding: 'utf-8', timeout: 5000 }).trim().split('\n')[0];
  } catch {}

  const claudeDir = join(homedir(), '.claude');

  try {
    const settings = JSON.parse(readFileSync(join(claudeDir, 'settings.json'), 'utf-8'));
    const plugins = Object.keys(settings.enabledPlugins ?? {}).filter(k => settings.enabledPlugins[k]);
    if (plugins.length) meta.enabled_plugins = plugins;
    if (settings.permissions?.defaultMode) meta.permissions_mode = settings.permissions.defaultMode;
  } catch {}

  try {
    const mcpCache = JSON.parse(readFileSync(join(claudeDir, 'mcp-needs-auth-cache.json'), 'utf-8'));
    const mcpNames = Object.keys(mcpCache);
    if (mcpNames.length) meta.mcp_servers = mcpNames;
  } catch {}

  try {
    const mcpList = execSync('claude mcp list 2>/dev/null', { encoding: 'utf-8', timeout: 10000 });
    const connected = mcpList.split('\n')
      .filter(l => l.includes('Connected'))
      .map(l => l.split(':')[0].trim())
      .filter(Boolean);
    if (connected.length) meta.mcp_servers_connected = connected;
  } catch {}

  try {
    const sessionsDir = join(claudeDir, 'sessions');
    const files = readdirSync(sessionsDir).filter(f => f.endsWith('.json')).slice(-5);
    for (const f of files) {
      const s = JSON.parse(readFileSync(join(sessionsDir, f), 'utf-8'));
      if (s.version) { meta.cc_version = meta.cc_version || s.version; break; }
    }
  } catch {}

  return meta;
}

async function fetchUserProfile(gatewayUrl: string, token: string, hasClaudeCode = true): Promise<{ tier: string; inference: string; localInference: boolean; captureDepth: string }> {
  try {
    const resp = await fetch(`${gatewayUrl}/api/v1/cli/me`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!resp.ok) return { tier: 'pro', inference: 'fast', localInference: false, captureDepth: 'full' };
    const data = await resp.json() as { fast_inference?: boolean; plan_tier?: string; local_inference?: boolean; capture_depth?: string };

    const meta = collectLocalMetadata(hasClaudeCode);
    fetch(`${gatewayUrl}/api/v1/cli/me`, {
      method: 'PATCH',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(meta),
    }).catch(() => {});

    return {
      tier: data.plan_tier ?? 'pro',
      inference: data.fast_inference ? 'fast' : 'standard',
      localInference: !!data.local_inference,
      captureDepth: data.capture_depth ?? 'full',
    };
  } catch {
    return { tier: 'pro', inference: 'fast', localInference: false, captureDepth: 'full' };
  }
}

// JWT-only auth — no API key minting. The hook scripts read the JWT from
// ~/.synkro/credentials.json and send it as Authorization: Bearer <jwt>.
// Auth middleware on the gateway resolves user/org from JWT claims via JWKS.

// The CLI ships the user's WorkOS Bearer JWT to the gateway URL. If a
// hostile actor sets --gateway=http://evil.example.com on a user's machine,
// the JWT lands at the attacker's server. Allow only:
//   - https://*.synkro.sh and synkro.sh apex
//   - http(s)://localhost or 127.0.0.1 (dev)
// Anything else is rejected before any fetch is attempted.
function assertGatewayAllowed(gatewayUrl: string): void {
  let parsed: URL;
  try { parsed = new URL(gatewayUrl); }
  catch { throw new Error(`Invalid gateway URL: ${gatewayUrl}`); }
  const proto = parsed.protocol;
  const host = parsed.hostname;
  if (proto !== 'http:' && proto !== 'https:') {
    throw new Error(`Gateway URL must be http(s); got ${proto}`);
  }
  const isLocalhost = host === 'localhost' || host === '127.0.0.1' || host === '::1';
  const isSynkro = host === 'synkro.sh' || host.endsWith('.synkro.sh');
  if (proto === 'http:' && !isLocalhost) {
    throw new Error(`Gateway URL must be HTTPS for non-localhost hosts; got ${gatewayUrl}`);
  }
  if (!isLocalhost && !isSynkro) {
    throw new Error(`Gateway host not in allowlist (synkro.sh or *.synkro.sh): ${host}`);
  }
}

// Detect a complete prior install: every hook script present, config.env
// written, and CC settings.json carries our `__synkro_managed__` markers.
// MCP registration is opt-out (--no-mcp) so we don't gate on it. Returns
// true only when every required surface is present so we never short-
// circuit a partial / broken install.
function isAlreadyInstalled(): boolean {
  const requiredScripts = [
    join(HOOKS_DIR, 'cc-bash-judge.ts'),
    join(HOOKS_DIR, 'cc-bash-followup.ts'),
    join(HOOKS_DIR, 'cc-edit-precheck.ts'),
    join(HOOKS_DIR, 'cc-cve-precheck.ts'),
    join(HOOKS_DIR, 'cc-plan-judge.ts'),
    join(HOOKS_DIR, 'cc-stop-summary.ts'),
    join(HOOKS_DIR, 'cc-session-start.ts'),
  ];
  if (!requiredScripts.every((p) => existsSync(p))) return false;
  if (!existsSync(CONFIG_PATH)) return false;

  const settingsPath = join(homedir(), '.claude', 'settings.json');
  if (!existsSync(settingsPath)) return false;
  try {
    const settings = JSON.parse(readFileSync(settingsPath, 'utf-8'));
    const hooks = settings?.hooks;
    if (!hooks || typeof hooks !== 'object') return false;
    const hasManaged = (kind: string) =>
      Array.isArray(hooks[kind]) &&
      hooks[kind].some((entry: Record<string, unknown>) => entry?.__synkro_managed__ === true);
    if (!hasManaged('PreToolUse')) return false;
    if (!hasManaged('PostToolUse')) return false;
    if (!hasManaged('SessionEnd')) return false;
    if (!hasManaged('SessionStart')) return false;
  } catch {
    return false;
  }
  return true;
}


export async function installCommand(opts: InstallOptions = {}): Promise<void> {
  // Synkro anchors all state — projects, telemetry, rulesets — to a repo
  // identity. Without a git repo there is nothing to key that state on, so a
  // repo is mandatory. This is a purely local check (`git remote get-url
  // origin`) — no GitHub login involved.
  if (!detectGitRepo()) {
    console.error('Synkro must be installed inside a git repository.');
    console.error('  `cd` into your project and re-run `synkro install`.');
    process.exit(1);
  }

  const gatewayUrl = opts.gatewayUrl
    || sanitizeGatewayCandidate(process.env.SYNKRO_GATEWAY_URL)
    || 'https://api.synkro.sh';

  // Reject hostile gateway overrides before we leak the JWT.
  try {
    assertGatewayAllowed(gatewayUrl);
  } catch (err) {
    console.error((err as Error).message);
    process.exit(1);
  }

  // No short-circuit. Plain `synkro install` is always a full refresh —
  // re-pulls the latest container image, re-writes hooks, re-registers the
  // MCP server. Each individual step is idempotent on its own; auth reuses
  // existing valid credentials so the user isn't bounced to the browser
  // unnecessarily.
  console.log('Synkro install starting...\n');

  // 1. Auth via browser OAuth (WorkOS). Persists JWT + refresh_token to
  //    ~/.synkro/credentials.json. The hook scripts authenticate against
  //    the gateway with `Authorization: Bearer <access_token>` — auth
  //    middleware resolves user/org from the JWT claims directly.
  if (!isAuthenticated()) {
    console.log('Opening browser for Synkro auth...');
    const result = await authenticate((status) => {
      switch (status.phase) {
        case 'starting':       console.log('  Starting local callback server...'); break;
        case 'browser-opened': console.log(`  Browser opened: ${status.url}`); break;
        case 'waiting':        console.log('  Waiting for browser auth to complete...'); break;
        case 'success':        console.log('  ✓ Authenticated'); break;
        case 'error':          console.error(`  ✗ ${status.message}`); break;
      }
    });
    if (!result) {
      console.error('Authentication failed. If you are running a self-hosted dashboard, set SYNKRO_WEB_AUTH_URL to its origin.');
      process.exit(1);
    }
  }
  const token = getAccessToken();
  if (!token) {
    console.error('No access token available after auth.');
    process.exit(1);
  }

  // 1b. GitHub PR scanning is hidden for now — the install no longer prompts
  // for it. The code path (connectGitHub / setupGithubCommand below) is kept
  // intact, just unreachable, for when PR scanning is re-enabled.
  const ghToken: string | null = null;

  // 1c. Connect repos (local git or GitHub OAuth)
  setApiBaseUrl(`${gatewayUrl}/api`);
  await promptRepoConnection({ linkRepo: opts.linkRepo });

  // 2. Detect installed agents + resolve config.
  //    If a valid .synkro file exists, skip all interactive prompts and derive
  //    agent selection, grading mode, and storage mode from it.
  const existingSynkro = readFullSynkroFile();
  const detected = detectAgents();

  let agents: DetectedAgent[];
  let gradingMode: 'local' | 'byok';
  let storageMode: 'local' | 'cloud';
  // The single user-facing axis: where the grading container runs.
  let deployLocation: 'local' | 'cloud' = 'local';
  // Per-source transcript-import consent. Defaults to NO import — embedding past
  // session history is privacy-sensitive and must be explicitly consented (the
  // prompt below sets these). Never import silently.
  let transcriptCC = false;
  let transcriptCursor = false;

  if (existingSynkro) {
    // .synkro exists — skip prompts, use its config
    const wantCC = existingSynkro.harness.includes('claude-code');
    const wantCursor = existingSynkro.harness.includes('cursor');
    agents = detected.filter(a =>
      (a.kind === 'claude_code' && wantCC) || (a.kind === 'cursor' && wantCursor)
    );
    if (agents.length === 0 && detected.length > 0) agents = detected;
    // location (new canonical) wins; fall back to legacy mode for old .synkro.
    deployLocation = existingSynkro.grader.location === 'cloud' ? 'cloud' : 'local';
    gradingMode = existingSynkro.grader.mode === 'byok' ? 'byok' : 'local';
    storageMode = deployLocation === 'cloud' ? 'cloud' : 'local';

    console.log(`Using .synkro config:`);
    for (const w of existingSynkro.warnings) console.warn(`  ⚠ ${w}`);
    console.log(`  harness: ${existingSynkro.harness.join(', ')}`);
    console.log(`  grading: ${gradingMode}    pool: ${poolLabel(existingSynkro.grader.pool)}`);
    for (const a of agents) {
      console.log(`  ✓ ${a.name}${a.version ? ` (${a.version})` : ''}`);
    }
    console.log();
  } else {
    // No .synkro — interactive prompts
    if (detected.length === 0) {
      console.error('No supported coding agents detected. Install Claude Code or Cursor first.');
      process.exit(1);
    }
    console.log('Detected agents:');
    for (const a of detected) {
      console.log(`  ✓ ${a.name}${a.version ? ` (${a.version})` : ''}`);
    }
    console.log();

    agents = await promptAgentSelection(detected);
    if (agents.length < detected.length) {
      console.log(`Installing hooks for: ${agents.map(a => a.name).join(', ')}\n`);
    }

    if (opts.byokGateway) {
      // Legacy escape hatch: no container, grading via the gateway with the
      // user's own provider key. Never offered in the prompt.
      gradingMode = 'byok'; storageMode = 'cloud'; deployLocation = 'local';
      console.log('  deploy: legacy BYOK-gateway grading (no container)\n');
    } else {
      deployLocation = await promptDeployLocation();
      gradingMode = 'local';
      storageMode = deployLocation === 'cloud' ? 'cloud' : 'local';
      console.log(`  deploy: ${deployLocation}\n`);
    }
  }

  // Resolve the deployment target. local → local Docker (today's path,
  // unchanged). cloud → a per-org Cloudflare container. The legacy no-container
  // BYOK-gateway path is selected only by --byok-gateway or an old .synkro
  // mode='byok' (never the prompt).
  type DeployTarget = 'local-docker' | 'cloud-container' | 'gateway-byok';
  const target: DeployTarget =
    deployLocation === 'cloud' ? 'cloud-container'
    : (opts.byokGateway || gradingMode === 'byok') ? 'gateway-byok'
    : 'local-docker';

  // Transcript-import consent — harness-aware, asked in BOTH paths. A .synkro
  // file configures harness/grading but NOT whether to embed past session
  // history, so importing must still be consented. Interactive → always prompt
  // (which sources). Non-interactive → no import unless explicitly opted in via
  // SYNKRO_IMPORT_TRANSCRIPTS=yes (privacy-safe: never process session data
  // silently).
  {
    const wantCC = agents.some(a => a.kind === 'claude_code');
    const wantCursor = agents.some(a => a.kind === 'cursor');
    if ((wantCC || wantCursor) && process.stdin.isTTY) {
      const src = await promptTranscriptSources(wantCC, wantCursor);
      transcriptCC = src.cc;
      transcriptCursor = src.cursor;
      const chosen = [src.cc && 'Claude Code', src.cursor && 'Cursor'].filter(Boolean).join(' + ');
      console.log(chosen ? `  ✓ Session import: ${chosen}\n` : '  ✗ Session import skipped\n');
    } else if ((wantCC || wantCursor) && process.env.SYNKRO_IMPORT_TRANSCRIPTS === 'yes') {
      transcriptCC = wantCC;
      transcriptCursor = wantCursor;
      console.log('  ✓ Session import: SYNKRO_IMPORT_TRANSCRIPTS=yes\n');
    } else if (!process.stdin.isTTY) {
      console.log('  ✗ Session import skipped (non-interactive; set SYNKRO_IMPORT_TRANSCRIPTS=yes to opt in)\n');
    }
  }

  // Combined consent for config + the ongoing-sync gate (either source on).
  const transcriptConsent = transcriptCC || transcriptCursor;

  // 3. Set up Synkro directory + hook scripts + grader daemon
  ensureSynkroDir();
  // Stub hooks are thin pipes to a LOCAL container on 18931 (all scanning brains
  // — route(), systemMessage shaping, rule fetch — live server-side there). Cloud
  // mode runs NO local container: the per-org grader is remote and the DO exposes
  // only the dispatcher (:8929/submit), not /api/scan/ (:8931). So stub hooks in
  // cloud fail open to `{}` — no systemMessage, no blocking. Full hooks carry the
  // route()+hostedGrade() logic inline (grade → containers.synkro.sh) and fall
  // back to the gateway for config/rules, so they're the ONLY mode that works in
  // cloud. Force it (an explicit --stub can't function against a remote grader).
  let hookMode = opts.hookMode || resolvePersistedHookMode();
  if (target === 'cloud-container' && hookMode === 'stub') {
    if (opts.hookMode === 'stub') {
      console.log('  ⓘ Cloud mode needs full hooks (stub hooks require a local container) — using full.\n');
    }
    hookMode = 'full';
  }
  const scripts = writeHookScripts(hookMode);
  console.log('Wrote hook scripts to ~/.synkro/hooks/\n');

  // Kill any stale legacy Python grader daemons so they don't keep handling
  // requests after we've switched to the local-CC channel path.
  for (const mode of ['edit', 'bash']) {
    const pidFile = join(SYNKRO_DIR, 'daemon', mode, 'daemon.pid');
    try {
      const pid = parseInt(readFileSync(pidFile, 'utf-8').trim(), 10);
      if (pid > 0) {
        process.kill(pid, 'SIGTERM');
        console.log(`Stopped stale ${mode} grader daemon (pid ${pid})`);
      }
    } catch {}
  }

  // 4. Configure CC hooks (atomic merge into settings.json).
  //    Edit/Write/MultiEdit/NotebookEdit fires a thin command shim that POSTs
  //    proposed content to /api/v1/precheck-edit. Server cosines the content
  //    against the org's active agent_runtime rules and returns the deterministic
  //    CC-hook JSON (deny + retry guidance, or empty allow). No LLM in the hook
  //    path — agent retries with a safer version on its own inference when it
  //    reads the denial reason.
  let hasClaudeCode = false;
  let hasCursor = false;
  for (const agent of agents) {
    if (agent.kind === 'claude_code') {
      hasClaudeCode = true;
      installCCHooks(agent.settingsPath, {
        bashJudgeScriptPath: scripts.bashScript,
        bashFollowupScriptPath: scripts.bashFollowupScript,
        editPrecheckScriptPath: scripts.editPrecheckScript,
        cwePrecheckScriptPath: scripts.cwePrecheckScript,
        cvePrecheckScriptPath: scripts.cvePrecheckScript,
        planJudgeScriptPath: scripts.planJudgeScript,
        agentJudgeScriptPath: scripts.agentJudgeScript,
        stopSummaryScriptPath: scripts.stopSummaryScript,
        sessionStartScriptPath: scripts.sessionStartScript,
        transcriptSyncScriptPath: scripts.transcriptSyncScript,
        userPromptSubmitScriptPath: scripts.userPromptSubmitScript,
        installScanScriptPath: scripts.installScanScript,
        taskActivateIntentScriptPath: scripts.taskActivateIntentScript,
        mcpGateScriptPath: scripts.mcpGateScript,
        skipTranscriptSync: !transcriptConsent,
      });
      console.log(`Configured ${agent.name} hooks at ${agent.settingsPath}`);
    } else if (agent.kind === 'cursor') {
      hasCursor = true;
      installCursorHooks(agent.settingsPath, {
        bashJudgeScriptPath: scripts.cursorBashJudgeScript,
        editCaptureScriptPath: scripts.cursorEditCaptureScript,
        agentCaptureScriptPath: scripts.cursorAgentCaptureScript,
        bashFollowupScriptPath: scripts.bashFollowupScript,
        editPrecheckScriptPath: scripts.editPrecheckScript,
        cwePrecheckScriptPath: scripts.cwePrecheckScript,
        cvePrecheckScriptPath: scripts.cvePrecheckScript,
        planJudgeScriptPath: scripts.planJudgeScript,
        agentJudgeScriptPath: scripts.agentJudgeScript,
        stopSummaryScriptPath: scripts.stopSummaryScript,
        sessionStartScriptPath: scripts.sessionStartScript,
        userPromptSubmitScriptPath: scripts.userPromptSubmitScript,
        transcriptSyncScriptPath: scripts.transcriptSyncScript,
        installScanScriptPath: scripts.installScanScript,
        mcpGateScriptPath: scripts.mcpGateScript,
      });
      console.log(`Configured ${agent.name} hooks at ${agent.settingsPath}`);
    }
  }
  console.log();

  // 5b. Fetch user profile early — we need captureDepth to decide local vs cloud MCP.
  let userId: string | undefined;
  let orgId: string | undefined;
  let email: string | undefined;
  try {
    const info = getUserInfo();
    userId = info.id;
    orgId = info.org_id;
    email = info.email;
  } catch {
    // unreachable — we just authenticated above
  }
  const profile = await fetchUserProfile(gatewayUrl, token, hasClaudeCode);
  // Cloud-only when both axes are cloud: BYOK grading + cloud storage bypass
  // the container's worker pool AND its PGLite, so skip Docker entirely and
  // register the MCP against the cloud endpoint. Any other combination still
  // needs the local container (for grading and/or local storage).
  // Local Docker is the ONLY target that runs a container on this machine.
  // cloud-container provisions a remote per-org CF container; gateway-byok runs
  // no container. Both register the cloud MCP and skip local Docker.
  const useLocalMcp = target === 'local-docker';
  if (target === 'cloud-container') {
    console.log('Cloud setup — Synkro will provision a private hosted container for your org.\n');
  } else if (target === 'gateway-byok') {
    console.log('Cloud-only setup (BYOK grading via gateway) — skipping the local container.\n');
  }

  // 5c. Register the Synkro Guardrails MCP server in ~/.claude.json.
  //     Local mode: point to localhost:8931, start local server, backfill rules.
  //     Cloud mode: mint long-lived JWT, point to cloud endpoint.
  if (hasClaudeCode && !opts.noMcp) {
    if (useLocalMcp) {
      try {
        const mintResp = await fetch(`${gatewayUrl}/api/v1/cli/mcp-token`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: '{}',
        });
        let mcpJwt = '';
        if (mintResp.ok) {
          const minted = await mintResp.json() as { token: string; expires_at: string };
          mcpJwt = minted.token;
          writeFileSync(join(SYNKRO_DIR, '.mcp-jwt'), mcpJwt + '\n', { mode: 0o600 });
        } else {
          console.warn('  ⚠ Could not mint MCP token — local server will reject requests until re-installed.');
        }
        const mcp = installMcpConfig({ gatewayUrl, bearerToken: mcpJwt, local: true });
        console.log(`Registered local MCP guardrails server in ${mcp.path}`);
        console.log(`  url:        ${mcp.url}`);
        console.log();
      } catch (err) {
        console.warn(`  ⚠ Local MCP setup failed: ${(err as Error).message}`);
        console.warn('  Hooks are still installed. Re-run `synkro install` to retry.');
        console.log();
      }
    } else {
      try {
        const mintResp = await fetch(`${gatewayUrl}/api/v1/cli/mcp-token`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: '{}',
        });
        if (!mintResp.ok) {
          const errText = await mintResp.text().catch(() => '');
          throw new Error(`mcp-token mint failed (${mintResp.status}): ${errText.slice(0, 200)}`);
        }
        const minted = await mintResp.json() as { token: string; expires_at: string };
        writeFileSync(join(SYNKRO_DIR, '.mcp-jwt'), minted.token + '\n', { mode: 0o600 });
        const mcp = installMcpConfig({ gatewayUrl, bearerToken: minted.token });
        console.log(`Registered Synkro guardrails MCP server in ${mcp.path}`);
        console.log(`  url:        ${mcp.url}`);
        console.log(`  expires:    ${minted.expires_at} (~1 year)`);
        console.log('  (restart any running Claude Code session for it to load)');
        console.log();
      } catch (err) {
        console.warn(`  ⚠ MCP registration failed: ${(err as Error).message}`);
        console.warn('  Hooks are still installed. Re-run `synkro install` to retry MCP setup.');
        console.log();
      }
    }
  }

  // 5d. Register the Synkro Guardrails MCP server in ~/.cursor/mcp.json.
  if (hasCursor && !opts.noMcp) {
    try {
      if (useLocalMcp) {
        // Ensure JWT exists (may already be minted in 5c for Claude Code)
        const jwtPath = join(SYNKRO_DIR, '.mcp-jwt');
        if (!existsSync(jwtPath)) {
          const mintResp = await fetch(`${gatewayUrl}/api/v1/cli/mcp-token`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: '{}',
          });
          if (mintResp.ok) {
            const minted = await mintResp.json() as { token: string; expires_at: string };
            writeFileSync(jwtPath, minted.token + '\n', { mode: 0o600 });
          }
        }
        const mcp = installCursorMcpConfig({ gatewayUrl, bearerToken: '', local: true });
        console.log(`Registered local MCP guardrails server in ${mcp.path}`);
        console.log(`  url:        ${mcp.url}`);
      } else {
        const mintResp = await fetch(`${gatewayUrl}/api/v1/cli/mcp-token`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: '{}',
        });
        if (!mintResp.ok) {
          const errText = await mintResp.text().catch(() => '');
          throw new Error(`mcp-token mint failed (${mintResp.status}): ${errText.slice(0, 200)}`);
        }
        const minted = await mintResp.json() as { token: string; expires_at: string };
        writeFileSync(join(SYNKRO_DIR, '.mcp-jwt'), minted.token + '\n', { mode: 0o600 });
        const mcp = installCursorMcpConfig({ gatewayUrl, bearerToken: minted.token });
        console.log(`Registered Synkro guardrails MCP server in ${mcp.path}`);
        console.log(`  url:        ${mcp.url}`);
      }
      console.log();
    } catch (err) {
      console.warn(`  ⚠ Cursor MCP registration failed: ${(err as Error).message}`);
      console.log();
    }
  }

  // 6. Write config.env — hook scripts read SYNKRO_GATEWAY_URL and the
  //    credentials path. They auth via Bearer JWT (resolved from the
  //    credentials file at runtime so refreshes Just Work).
  const synkroBundle = resolveSynkroBundle();
  // Persist the deployment mode so subsequent installs / hooks see the same
  // value without depending on shell-sourced env.
  const persistedMode = resolveDeploymentMode();
  writeConfigEnv({ gatewayUrl, userId, orgId, email, tier: profile.tier, inference: profile.inference, synkroBin: synkroBundle, transcriptConsent, localInference: profile.localInference, deploymentMode: persistedMode, gradingMode, storageMode, deployLocation, hookMode });
  console.log(`Wrote config to ${CONFIG_PATH}`);
  console.log(`  inference: ${profile.inference} (server-side grading)`);
  if (profile.localInference) console.log(`  local inference: enabled (gradingProvider=claude-code)`);
  if (synkroBundle) console.log(`  SYNKRO_CLI_BIN=${synkroBundle}`);
  else console.warn('  ⚠ Could not resolve synkro bundle path; hooks will fall back to PATH lookup of `synkro`.');

  try {
    const prompts = await fetchJudgePrompts({ gatewayUrl, jwt: token });
    console.log(`  prompts: ${prompts.version} (live)`);
  } catch (err) {
    console.warn(`  ⚠ Could not cache judge prompts: ${(err as Error).message}`);
  }

  // Write synkro.toml to repo root if one doesn't exist yet — LOCAL only. In
  // cloud mode application is fully server-side (org standards + pool live in the
  // dashboard), so there's no repo config to write; the harness enforces by org
  // wherever Synkro is installed.
  if (deployLocation !== 'cloud') {
    writeSynkroFileIfMissing({ hasClaudeCode, hasCursor, gradingMode, deployLocation });
  } else {
    console.log('  Cloud mode: standards + grader pool are configured in the dashboard');
    console.log('  (Settings → Standards / Pool) — no synkro.toml needed.');
  }
  console.log();

  // Tracks whether the cloud grader verified end-to-end (pueue+tmux smoke grade).
  // null = not a cloud install / not checked; false = verified and FAILED.
  let cloudGradeOk: boolean | null = null;

  // The container provides the grading worker pool and local PGLite — needed
  // for any setup except cloud-only (BYOK grading + cloud storage).
  if (useLocalMcp) {
    const { assertDockerAvailable } = await import('../local-cc/dockerInstall.js');
    try {
      assertDockerAvailable();
    } catch (err) {
      console.error(`\n✗ ${(err as Error).message}`);
      process.exit(1);
    }

    // Cursor worker pool needs a Cursor API key — collect it before starting
    // the container so the workers come up authenticated.
    if (hasCursor) {
      await promptCursorApiKey(opts);
    }

    console.log('Installing Synkro server container...');
    // Read .synkro for worker config: explicit counts > pool > agent detection.
    const sf = readFullSynkroFile();
    for (const w of sf?.warnings || []) console.warn(`  ⚠ ${w}`);
    let claudeWorkers: number;
    let cursorWorkers: number;
    if (sf && (sf.workers.claude != null || sf.workers.cursor != null)) {
      claudeWorkers = Math.max(0, Math.floor(sf.workers.claude || 0));
      cursorWorkers = Math.max(0, Math.floor(sf.workers.cursor || 0));
      if (claudeWorkers + cursorWorkers === 0) { claudeWorkers = 0; cursorWorkers = 8; }
      console.log(`  synkro.toml: explicit workers — ${claudeWorkers} claude + ${cursorWorkers} cursor`);
    } else {
      const totalWorkers = parseInt(process.env.SYNKRO_WORKERS_PER_POOL || '8', 10);
      const synkroFilePool = sf?.grader?.pool || 'auto';
      let providers: WorkerProvider[] = [];
      if (synkroFilePool === 'cursor') {
        providers = ['cursor'];
      } else if (synkroFilePool === 'claude_code') {
        providers = ['claude_code'];
      } else {
        if (hasClaudeCode) providers.push('claude_code');
        if (hasCursor) providers.push('cursor');
      }
      ({ claudeWorkers, cursorWorkers } = splitWorkers(totalWorkers, providers));
      if (synkroFilePool !== 'auto') console.log(`  synkro.toml: grader pool set to ${poolLabel(synkroFilePool)}`);
    }
    console.log(`  worker pool: ${claudeWorkers} claude + ${cursorWorkers} cursor`);
    // Connected repo — the container names the seeded app + ruleset after it.
    const connectedRepo = detectGitRepo() || undefined;
    const { image, hostMcpPort, hostGraderPort, hostCwePort, hostPglitePort } =
      await dockerInstall({ claudeWorkers, cursorWorkers, connectedRepo });
    console.log(`  ✓ pulled ${image}`);
    console.log(`  container started — MCP=${hostMcpPort} general=${hostGraderPort} CWE=${hostCwePort} pglite=${hostPglitePort}`);
    console.log('  waiting for container to be ready...');
    const ready = await waitForContainerReady(60_000);
    if (ready) {
      console.log('  ✓ container ready');
      const mcpJwt = readFileSync(join(SYNKRO_DIR, '.mcp-jwt'), 'utf-8').trim();
      try {
        const ingestResp = await fetch(`http://127.0.0.1:${hostMcpPort}/api/ingest`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${mcpJwt}` },
          body: JSON.stringify({ capture_type: 'healthcheck', event_id: `healthcheck_${Date.now()}` }),
          signal: AbortSignal.timeout(5000),
        });
        if (ingestResp.ok) {
          console.log('  ✓ ingest endpoint verified');
        } else {
          console.warn(`  ⚠ ingest endpoint returned ${ingestResp.status} — telemetry spool may not drain.`);
          console.warn('    The .mcp-jwt token may not match the container. Try: synkro uninstall && synkro install');
        }
      } catch {
        console.warn('  ⚠ ingest endpoint unreachable — telemetry spool may not drain.');
      }
      const workersUp = await waitForWorkersReady(30_000);
      if (workersUp) {
        console.log('  ✓ workers ready');
        await syncSkillFiles();
      } else {
        console.warn('  ⚠ workers did not register within 30s — skill sync skipped');
      }
    } else {
      console.error('  ✗ container did not become healthy within 60s');
      console.error('    Run `docker logs synkro-server` to debug.');
      process.exit(1);
    }
    console.log();
  } else if (target === 'cloud-container') {
    // Cloud: capture the org's Claude setup-token (CLOUD ONLY) and hand it to
    // the gateway, which stores it as the org secret and provisions the per-org
    // Cloudflare container. No local container, no keychain, no mounts.
    // Cursor pool? Collect the Cursor API key first so it's bundled into the
    // provision (cloud cursor workers grade with it).
    if (hasCursor) await promptCursorApiKey(opts);
    await provisionCloudContainer({ gatewayUrl, jwt: token, orgId, userId, email, hasClaudeCode, hasCursor });
    // Warm + smoke-test the hosted grader (pueue+tmux channel grade) so a broken
    // container/token surfaces HERE, not silently on the user's first edit. The
    // result gates the final success message below — a failed grade is a failed
    // install, not a clean one.
    cloudGradeOk = await verifyCloudGrader(token);
  }

  // 7. Import + embed session transcripts for the consented source(s).
  if (transcriptConsent) {
    const repo = detectGitRepo();
    if (repo) {
      if (storageMode === 'local') {
        // Local: sync into PGLite — the container's embedding model vectors each message.
        try {
          let mcpToken = '';
          try { mcpToken = readFileSync(join(SYNKRO_DIR, '.mcp-jwt'), 'utf-8').trim(); } catch {}
          if (mcpToken) {
            const mcpPort = parseInt(process.env.SYNKRO_MCP_PORT || '18931', 10);
            let imported = 0;
            if (transcriptCC && hasClaudeCode) {
              const r = await syncTranscriptsLocal(mcpPort, mcpToken, repo);
              if (r.messages > 0) {
                console.log(`  ✓ Imported ${r.sessions} Claude Code sessions (${r.messages} messages) into local store.`);
                imported += r.messages;
              }
            }
            if (transcriptCursor && hasCursor) {
              const r = await syncCursorTranscriptsLocal(mcpPort, mcpToken, repo);
              if (r.messages > 0) {
                console.log(`  ✓ Imported ${r.sessions} Cursor sessions (${r.messages} messages) into local store.`);
                imported += r.messages;
              }
            }
            if (imported > 0) {
              console.log('    Embeddings generated. Analyzing for rule suggestions...');
              try {
                const suggestResp = await fetch(`http://127.0.0.1:${mcpPort}/api/local/suggest-rules`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${mcpToken}` },
                  signal: AbortSignal.timeout(90000),
                });
                if (suggestResp.ok) {
                  const suggestResult = await suggestResp.json() as { suggested?: number; skippedDuplicates?: number };
                  if (suggestResult.suggested && suggestResult.suggested > 0) {
                    console.log(`  ✓ Generated ${suggestResult.suggested} rule suggestions from your session history.`);
                    console.log('    Ask Synkro: "show me suggested rules" to review them.\n');
                  } else {
                    console.log('    No rule suggestions generated yet — more session data needed.\n');
                  }
                }
              } catch {
                console.log('    Rule analysis will run automatically as more sessions are recorded.\n');
              }
            }
          } else {
            console.warn('  ⚠ Session import skipped — container auth token not found.\n');
          }
        } catch (err) {
          console.warn(`  ⚠ Local session import failed: ${(err as Error).message}\n`);
        }
      } else {
        // Cloud (Timescale via gateway): Claude Code transcripts. Cursor cloud
        // backfill isn't wired yet — Cursor import currently requires local storage.
        if (transcriptCC && hasClaudeCode) {
          try {
            const ingested = await ingestSessionTranscripts(gatewayUrl, token, repo);
            if (ingested > 0) {
              console.log(`  ✓ Indexed ${ingested} session insights from Claude Code history.`);
            }
          } catch (err) {
            console.warn(`  ⚠ Session indexing skipped: ${(err as Error).message}\n`);
          }
          try {
            const result = await syncTranscriptsBulk(gatewayUrl, token, repo);
            if (result.messages > 0) {
              console.log(`  ✓ Synced ${result.sessions} sessions (${result.messages} messages) to cloud.`);
              console.log('    Embeddings use your configured inference provider.\n');
            }
          } catch (err) {
            console.warn(`  ⚠ Transcript sync skipped: ${(err as Error).message}\n`);
          }
        }
        if (transcriptCursor && hasCursor) {
          console.log('  ℹ Cursor transcript import requires local storage mode — skipped under cloud storage.\n');
        }
      }
    }
  }

  // 8. PR scan setup (secrets + workflow) — only if GitHub was connected in step 1b
  if (ghToken) {
    const { setupGithubCommand } = await import('./setupGithub.js');
    await setupGithubCommand({ nonInteractive: true, githubToken: ghToken });
  }

  // 9. Done — but a cloud install whose grader didn't verify is NOT a success.
  if (cloudGradeOk === false) {
    console.error('');
    console.error('✗ Synkro installed, but the cloud grader is NOT working — grading will fail on your edits.');
    console.error('  The smoke grade above shows why (most often a bad/incomplete Claude setup-token → 401).');
    console.error('  Re-run `synkro install` → cloud to re-authorize with a fresh token.');
    process.exitCode = 1;
  } else {
    console.log('✓ Synkro installed.');
  }

  // 10. Claude Desktop observability (macOS) is a deliberate, SEPARATE action —
  // install must return cleanly, not hand off into a long-running foreground MITM
  // tap. So we don't start it here; we just point the user at the command. The tap
  // (session-scoped local proxy → conversations in the dashboard) runs only via
  // `synkro claude-desktop`, where it owns the foreground until Ctrl+C.
  if (process.stdin.isTTY && claudeDesktopInstalled()) {
    console.log('\n  Claude Desktop detected — to monitor its conversations in your dashboard, run: synkro claude-desktop');
  }
}

/**
 * Identify the git repo `synkro install` is running in. `git` itself walks up
 * from the cwd, so this resolves correctly from any subdirectory or worktree.
 * Tries the `origin` remote (→ owner/repo), then falls back to the repo's
 * top-level directory name. Returns null only when the cwd is not a git repo
 * at all — mirrors the hook's detectRepo() so the seeded project id and the id
 * telemetry lands under always agree.
 */
// Coerce a single TOML value token ("local", 8, true, ["a","b"]) to JS.
function parseTomlValue(raw: string): any {
  const v = raw.trim();
  if (v.startsWith('[') && v.endsWith(']')) {
    const inner = v.slice(1, -1).trim();
    if (!inner) return [];
    return inner.split(',').map(s => parseTomlValue(s)).filter(s => s !== '');
  }
  if ((v.startsWith('"') && v.endsWith('"')) || (v.startsWith("'") && v.endsWith("'"))) return v.slice(1, -1);
  if (v === 'true') return true;
  if (v === 'false') return false;
  if (/^-?\d+$/.test(v)) return parseInt(v, 10);
  return v;
}

// Minimal TOML reader for synkro.toml's fixed, flat schema: top-level
// scalars/arrays plus the [grader] and [scanning] tables. Not a general TOML
// parser — we own the writer, so input shape is bounded. Returns the same
// nested-object shape the old YAML parser did, so downstream consumers are
// unchanged.
function parseSynkroToml(raw: string): Record<string, any> {
  const result: Record<string, any> = {};
  let section: string | null = null;
  for (const rawLine of raw.split('\n')) {
    const line = rawLine.trim();
    if (!line || line.startsWith('#')) continue;
    const sec = line.match(/^\[([^\]]+)\]$/);
    if (sec) {
      section = sec[1].trim();
      if (!result[section]) result[section] = {};
      continue;
    }
    const eq = line.indexOf('=');
    if (eq === -1) continue;
    const key = line.slice(0, eq).trim().replace(/^["']|["']$/g, '');
    let valRaw = line.slice(eq + 1).trim();
    // Strip an inline comment only off bare scalars — never inside a quoted
    // string or an inline array (where a '#' would be data).
    if (!valRaw.startsWith('"') && !valRaw.startsWith("'") && !valRaw.startsWith('[')) {
      const h = valRaw.indexOf('#');
      if (h !== -1) valRaw = valRaw.slice(0, h).trim();
    }
    const value = parseTomlValue(valRaw);
    if (section) (result[section] as Record<string, any>)[key] = value;
    else result[key] = value;
  }
  return result;
}

function writeSynkroFileIfMissing(opts: { hasClaudeCode: boolean; hasCursor: boolean; gradingMode: string; deployLocation?: 'local' | 'cloud' }): void {
  try {
    const root = execSync('git rev-parse --show-toplevel', { encoding: 'utf-8', timeout: 3000, stdio: ['pipe', 'pipe', 'pipe'] }).trim();
    if (!root) return;
    // Never treat $HOME as a repo to onboard: ~/.synkro is Synkro's own config
    // DIRECTORY, so writing config there would collide with it and an existence
    // check would always "find" it. (git rev-parse can resolve to $HOME when
    // dotfiles are git-tracked or cwd is a non-git dir under home.)
    if (root === homedir()) return;
    const fp = join(root, 'synkro.toml');
    // Onboarded only if a real synkro.toml FILE exists — statSync().isFile() so
    // a directory (or any non-file) never counts as "already configured".
    let hasFile = false;
    try { hasFile = statSync(fp).isFile(); } catch {}
    if (hasFile) {
      console.log(`  synkro.toml: ${fp} (existing, respected)`);
      return;
    }
    let pool: string = 'auto';
    const harness: string[] = [];
    if (opts.hasClaudeCode) { harness.push('claude-code'); if (!opts.hasCursor) pool = 'claude'; }
    if (opts.hasCursor) { harness.push('cursor'); if (!opts.hasClaudeCode) pool = 'cursor'; }
    const mode = opts.gradingMode === 'byok' ? 'byok' : 'local';
    // TOML scope rule: all top-level keys MUST precede any [table] header, or
    // they'd bind to the open table. Emit version/harness first, then the [grader]
    // and [scanning] tables. (Rulesets + skills are managed in the registry/dashboard
    // now, not via synkro.toml — so they're no longer written here.)
    const toml = [
      'version = 1',
      '',
      `harness = [${harness.map(h => `"${h}"`).join(', ')}]`,
      '',
      '[grader]',
      `location = "${opts.deployLocation || 'local'}"`,
      `pool = "${pool}"`,
      `mode = "${mode}"`,
      '',
      '[scanning]',
      'cwe = true',
      'cve = true',
      '',
    ].join('\n');
    writeFileSync(fp, toml, 'utf-8');
    console.log(`  synkro.toml: wrote ${fp} (pool=${pool}, mode=${mode})`);
  } catch {}
}

interface SynkroFile {
  harness: ('claude-code' | 'cursor')[];
  grader: { pool: 'auto' | 'claude_code' | 'cursor'; mode: string; location?: 'local' | 'cloud' };
  workers: { claude?: number; cursor?: number };
  /** Config-parse warnings (unrecognized provider tokens) to surface to the user. */
  warnings: string[];
  ruleset: string;
  skills: string[];
  scanning: { cwe: boolean; cve: boolean };
  /** Registry subscriptions: "namespace/name" -> version (e.g. "v3" | "latest"). */
  standards: Record<string, string>;
  _repoRoot: string;
}

export function readFullSynkroFile(): SynkroFile | null {
  try {
    const root = execSync('git rev-parse --show-toplevel', { encoding: 'utf-8', timeout: 3000, stdio: ['pipe', 'pipe', 'pipe'] }).trim();
    if (!root) return null;
    const fp = join(root, 'synkro.toml');
    if (!existsSync(fp)) return null;
    const parsed = parseSynkroToml(readFileSync(fp, 'utf-8'));
    const valid = ['claude-code', 'cursor'] as const;
    const harness = Array.isArray(parsed.harness)
      ? parsed.harness.filter((h: string) => valid.includes(h as any))
      : ['claude-code', 'cursor'];
    const resolved = resolveGraderPool(parsed);
    return {
      harness: harness.length > 0 ? harness : ['claude-code', 'cursor'],
      grader: {
        pool: resolved.pool,
        mode: ['local', 'byok'].includes(parsed.grader?.mode) ? parsed.grader.mode : 'local',
        location: parsed.grader?.location === 'cloud' ? 'cloud' : 'local',
      },
      workers: {
        ...(resolved.claudeWorkers != null ? { claude: resolved.claudeWorkers } : {}),
        ...(resolved.cursorWorkers != null ? { cursor: resolved.cursorWorkers } : {}),
      },
      warnings: resolved.warnings,
      ruleset: parsed.ruleset || 'default',
      skills: Array.isArray(parsed.skills) ? parsed.skills.filter((s: unknown) => typeof s === 'string' && s.endsWith('.md')) : [],
      scanning: { cwe: parsed.scanning?.cwe !== false, cve: parsed.scanning?.cve !== false },
      standards: (parsed.standards && typeof parsed.standards === 'object')
        ? Object.fromEntries(Object.entries(parsed.standards as Record<string, unknown>)
            .filter(([k, v]) => typeof v === 'string' && k.includes('/'))
            .map(([k, v]) => [k, String(v)]))
        : {},
      _repoRoot: root,
    };
  } catch { return null; }
}

export function reconcileHarness(): { claudeWorkers: number; cursorWorkers: number } | null {
  const sf = readFullSynkroFile();
  if (!sf) {
    console.log('No .synkro file found in repo root — skipping harness reconciliation.');
    return null;
  }

  const wantCC = sf.harness.includes('claude-code');
  const wantCursor = sf.harness.includes('cursor');
  for (const w of sf.warnings) console.warn(`  ⚠ ${w}`);
  console.log(`synkro.toml: harness=[${sf.harness.join(', ')}] pool=${poolLabel(sf.grader.pool)} mode=${sf.grader.mode}`);

  const scripts = writeHookScripts(resolvePersistedHookMode());
  console.log('Wrote hook scripts to ~/.synkro/hooks/');

  const ccSettings = join(homedir(), '.claude', 'settings.json');
  if (wantCC) {
    installCCHooks(ccSettings, {
      bashJudgeScriptPath: scripts.bashScript,
      bashFollowupScriptPath: scripts.bashFollowupScript,
      editPrecheckScriptPath: scripts.editPrecheckScript,
      cwePrecheckScriptPath: scripts.cwePrecheckScript,
      cvePrecheckScriptPath: scripts.cvePrecheckScript,
      planJudgeScriptPath: scripts.planJudgeScript,
      agentJudgeScriptPath: scripts.agentJudgeScript,
      stopSummaryScriptPath: scripts.stopSummaryScript,
      sessionStartScriptPath: scripts.sessionStartScript,
      transcriptSyncScriptPath: scripts.transcriptSyncScript,
      userPromptSubmitScriptPath: scripts.userPromptSubmitScript,
      installScanScriptPath: scripts.installScanScript,
      taskActivateIntentScriptPath: scripts.taskActivateIntentScript,
      mcpGateScriptPath: scripts.mcpGateScript,
    });
    console.log('  ✓ Claude Code hooks registered');
    try {
      const mcpJwt = readFileSync(join(SYNKRO_DIR, '.mcp-jwt'), 'utf-8').trim();
      if (mcpJwt) {
        installMcpConfig({ gatewayUrl: '', bearerToken: mcpJwt, local: true });
        console.log('  ✓ Claude Code MCP registered');
      }
    } catch {}
  } else {
    if (uninstallCCHooks(ccSettings)) console.log('  ✗ Claude Code hooks removed');
    if (uninstallMcpConfig()) console.log('  ✗ Claude Code MCP removed');
    if (uninstallClaudeDesktopMcpConfig()) console.log('  ✗ Claude Desktop MCP removed');
  }

  const cursorHooks = join(homedir(), '.cursor', 'hooks.json');
  if (wantCursor) {
    installCursorHooks(cursorHooks, {
      bashJudgeScriptPath: scripts.cursorBashJudgeScript,
      editCaptureScriptPath: scripts.cursorEditCaptureScript,
      agentCaptureScriptPath: scripts.cursorAgentCaptureScript,
      bashFollowupScriptPath: scripts.bashFollowupScript,
      editPrecheckScriptPath: scripts.editPrecheckScript,
      cwePrecheckScriptPath: scripts.cwePrecheckScript,
      cvePrecheckScriptPath: scripts.cvePrecheckScript,
      planJudgeScriptPath: scripts.planJudgeScript,
      agentJudgeScriptPath: scripts.agentJudgeScript,
      stopSummaryScriptPath: scripts.stopSummaryScript,
      sessionStartScriptPath: scripts.sessionStartScript,
      userPromptSubmitScriptPath: scripts.userPromptSubmitScript,
      transcriptSyncScriptPath: scripts.transcriptSyncScript,
      installScanScriptPath: scripts.installScanScript,
      mcpGateScriptPath: scripts.mcpGateScript,
    });
    console.log('  ✓ Cursor hooks registered');
    try {
      installCursorMcpConfig({ gatewayUrl: '', bearerToken: '', local: true });
      console.log('  ✓ Cursor MCP registered');
    } catch {}
  } else {
    if (uninstallCursorHooks(cursorHooks)) console.log('  ✗ Cursor hooks removed');
    if (uninstallCursorMcpConfig()) console.log('  ✗ Cursor MCP removed');
  }

  // Explicit worker counts from .synkro take priority
  if (sf.workers.claude != null || sf.workers.cursor != null) {
    const cw = Math.max(0, Math.floor(sf.workers.claude || 0));
    const curw = Math.max(0, Math.floor(sf.workers.cursor || 0));
    if (cw + curw > 0) return { claudeWorkers: cw, cursorWorkers: curw };
  }

  const total = parseInt(process.env.SYNKRO_WORKERS_PER_POOL || '8', 10);
  const providers: WorkerProvider[] = [];
  if (sf.grader.pool === 'cursor') {
    providers.push('cursor');
  } else if (sf.grader.pool === 'claude_code') {
    providers.push('claude_code');
  } else {
    if (wantCC) providers.push('claude_code');
    if (wantCursor) providers.push('cursor');
  }
  if (providers.length === 0) providers.push('claude_code');
  return splitWorkers(total, providers);
}

export async function syncSkillFiles(): Promise<void> {
  const sf = readFullSynkroFile();
  if (!sf || sf.skills.length === 0) return;

  const resolved = resolveSkillPaths(sf.skills, sf._repoRoot);
  if (resolved.length === 0) return;

  const mcpPort = process.env.SYNKRO_MCP_PORT || '18931';
  const tasks = resolved.map(fp => {
    const content = readFileSync(fp, 'utf-8');
    const source = `skill:${fp.split('/').pop()}`;
    if (!content.trim()) {
      console.log(`  ⊘ skill ${source}: empty file, skipped`);
      return null;
    }
    const lines = content.split('\n').length;
    console.log(`  → read ${source} (${lines} lines, ${(content.length / 1024).toFixed(1)} KB)`);
    return { source, content };
  }).filter(Boolean) as { source: string; content: string }[];

  if (tasks.length === 0) return;

  console.log(`  indexing ${tasks.length} skill file${tasks.length === 1 ? '' : 's'} — comparing against existing ruleset (this may take a few seconds)...`);

  // Sequential, NOT parallel: each file must dedup against the rules the
  // previous files just added, otherwise two md files containing the same rule
  // would both read the pre-existing ruleset and each add it (a cross-file
  // dupe). Processing one at a time means file N sees files 1..N-1's additions.
  // The grader call is light now, so the wall-clock cost is small.
  for (const { source, content } of tasks) {
    try {
      const resp = await fetch(`http://127.0.0.1:${mcpPort}/api/local/skills/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ source, content }),
        signal: AbortSignal.timeout(65000),
      });
      if (!resp.ok) throw new Error(`${resp.status}`);
      const { created, message } = await resp.json() as { created: number; message?: string };
      console.log(created > 0
        ? `  ✓ skill ${source}: ${created} new rule${created === 1 ? '' : 's'} added to index`
        : `  ✓ skill ${source}: ${message || 'up to date'} — index unchanged`);
    } catch (e) {
      console.warn(`  ⚠ skill ${source}: sync failed (${e instanceof Error ? e.message : String(e)})`);
    }
  }
}

export function detectGitRepo(): string | null {
  const run = (cmd: string): string => {
    try {
      return execSync(cmd, { encoding: 'utf-8', timeout: 5000, stdio: ['pipe', 'pipe', 'pipe'] }).trim();
    } catch {
      return '';
    }
  };
  const remoteUrl = run('git remote get-url origin');
  if (remoteUrl) {
    return remoteUrl
      .replace(/^git@[^:]+:/, '')
      .replace(/^https?:\/\/[^/]+\//, '')
      .replace(/\.git$/, '');
  }
  // No origin remote — fall back to the repo's top-level directory name.
  const root = run('git rev-parse --show-toplevel');
  return root ? (root.split('/').pop() || null) : null;
}

function getClaudeProjectsFolder(): string | null {
  const cwd = process.cwd();
  // CC stores transcripts in ~/.claude/projects/{sanitized-path}/
  // where sanitized-path replaces / with -
  const sanitized = '-' + cwd.replace(/\//g, '-');
  const projectsDir = join(homedir(), '.claude', 'projects', sanitized);
  return existsSync(projectsDir) ? projectsDir : null;
}

interface SessionInsight {
  session_id: string;
  insight_type: 'summary' | 'user_message';
  content: string;
  metadata?: Record<string, unknown>;
}

function extractSessionInsights(projectsDir: string): SessionInsight[] {
  const insights: SessionInsight[] = [];
  const files = readdirSync(projectsDir).filter(f => f.endsWith('.jsonl'));

  for (const file of files) {
    const sessionId = file.replace('.jsonl', '');
    const filePath = join(projectsDir, file);

    try {
      const content = readFileSync(filePath, 'utf-8');
      const lines = content.split('\n').filter(Boolean);

      // Extract compaction summaries
      for (let i = 0; i < lines.length; i++) {
        try {
          const entry = JSON.parse(lines[i]);
          if (entry.type === 'user' &&
              typeof entry.message?.content === 'string' &&
              entry.message.content.startsWith('This session is being continued')) {
            insights.push({
              session_id: sessionId,
              insight_type: 'summary',
              content: entry.message.content.slice(0, 4000),
              metadata: { source: 'compaction_summary' },
            });
          }
        } catch {}
      }

      // Extract user messages (last 20 per session — recent preferences)
      const userMessages: string[] = [];
      for (let i = lines.length - 1; i >= 0 && userMessages.length < 20; i--) {
        try {
          const entry = JSON.parse(lines[i]);
          if (entry.type === 'user') {
            const text = typeof entry.message?.content === 'string'
              ? entry.message.content
              : Array.isArray(entry.message?.content)
                ? entry.message.content.map((b: any) => b.text ?? b).filter((t: any) => typeof t === 'string').join(' ')
                : null;
            if (text && text.length > 10 && text.length < 2000 && !text.startsWith('This session is being continued')) {
              userMessages.push(text);
            }
          }
        } catch {}
      }
      for (const msg of userMessages.reverse()) {
        insights.push({
          session_id: sessionId,
          insight_type: 'user_message',
          content: msg.slice(0, 2000),
        });
      }
    } catch {}
  }

  return insights;
}

async function ingestSessionTranscripts(gatewayUrl: string, token: string, repo: string): Promise<number> {
  const projectsDir = getClaudeProjectsFolder();
  if (!projectsDir) return 0;

  const insights = extractSessionInsights(projectsDir);
  if (insights.length === 0) return 0;

  console.log(`Found ${insights.length} session insights from Claude Code history...`);

  // Send in batches of 100
  let total = 0;
  for (let i = 0; i < insights.length; i += 100) {
    const batch = insights.slice(i, i + 100);
    try {
      const resp = await fetch(`${gatewayUrl}/api/v1/cli/ingest-sessions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ repo, sessions: batch }),
      });
      if (resp.ok) {
        const result = await resp.json() as { accepted: number };
        total += result.accepted;
      }
    } catch {}
  }

  return total;
}

interface TranscriptMessage {
  message_index: number;
  type: 'user' | 'assistant';
  content: string;
  tool_calls?: Array<{ name: string; input: string; id: string }>;
  model?: string;
  usage?: {
    input_tokens?: number;
    output_tokens?: number;
    cache_creation_input_tokens?: number;
    cache_read_input_tokens?: number;
  };
}

function extractTextContent(content: any): string {
  if (typeof content === 'string') return content.slice(0, 8000);
  if (Array.isArray(content)) {
    return content
      .filter((b: any) => typeof b === 'string' || (b?.type === 'text'))
      .map((b: any) => typeof b === 'string' ? b : (b?.text || ''))
      .join(' ')
      .slice(0, 8000);
  }
  return '';
}

// ─── Cursor transcript import ────────────────────────────────────────────────
// Cursor stores transcripts at
//   ~/.cursor/projects/{slug}/agent-transcripts/{convId}/{convId}.jsonl
// where each line is {role:'user'|'assistant', message:{content:[{type:'text',
// text}]}} — note `role` is top-level (vs CC's {type, message:{role,...}}).
// The slug is the workspace path with non-alphanumerics collapsed to '-'.
function cursorProjectSlug(workspaceRoot: string): string {
  return workspaceRoot.replace(/^[/]+/, '').replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

function getCursorTranscriptsDir(): string | null {
  const dir = join(homedir(), '.cursor', 'projects', cursorProjectSlug(process.cwd()), 'agent-transcripts');
  return existsSync(dir) ? dir : null;
}

// Conversation ids are UUIDs. CWE-22: validate against a strict allowlist before
// using a dir name in any path join, so a crafted entry (.., /) can't traverse.
function isSafeConvId(id: string): boolean {
  return /^[A-Za-z0-9_-]+$/.test(id);
}

function parseCursorTranscriptFile(filePath: string): TranscriptMessage[] {
  const content = readFileSync(filePath, 'utf-8');
  const lines = content.split('\n').filter(Boolean);
  const messages: TranscriptMessage[] = [];
  for (let i = 0; i < lines.length; i++) {
    try {
      const entry = JSON.parse(lines[i]);
      const role = entry.role || entry.message?.role;
      if (role !== 'user' && role !== 'assistant') continue;
      const text = extractTextContent(entry.message?.content ?? entry.content);
      if (!text) continue;
      messages.push({ message_index: i, type: role, content: text });
    } catch {}
  }
  return messages;
}

async function syncCursorTranscriptsLocal(mcpPort: number, mcpToken: string, repo: string): Promise<{ sessions: number; messages: number }> {
  const dir = getCursorTranscriptsDir();
  if (!dir) return { sessions: 0, messages: 0 };
  // Each conversation is a subdir {convId}/ holding {convId}.jsonl.
  let convDirs: string[] = [];
  try {
    convDirs = readdirSync(dir, { withFileTypes: true })
      .filter(d => d.isDirectory() && isSafeConvId(d.name))
      .map(d => d.name);
  } catch { return { sessions: 0, messages: 0 }; }
  if (convDirs.length === 0) return { sessions: 0, messages: 0 };

  console.log(`  Found ${convDirs.length} Cursor session transcripts, importing + embedding...`);
  let totalSessions = 0;
  let totalMessages = 0;

  for (let i = 0; i < convDirs.length; i++) {
    const convId = convDirs[i];
    if (!isSafeConvId(convId)) continue; // defense-in-depth before any path join
    const filePath = join(dir, convId, `${convId}.jsonl`);
    if (!existsSync(filePath)) continue;
    try {
      const all = parseCursorTranscriptFile(filePath);
      const messages = all.length > 500 ? all.slice(-500) : all;
      if (messages.length === 0) continue;
      const resp = await fetch(`http://127.0.0.1:${mcpPort}/api/conversation-sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${mcpToken}` },
        body: JSON.stringify({ session_id: convId, repo, messages }),
        signal: AbortSignal.timeout(15000),
      });
      if (resp.ok) {
        const result = await resp.json() as { ingested?: number };
        totalSessions++;
        totalMessages += result.ingested ?? messages.length;
      }
    } catch {}

    if ((i + 1) % 10 === 0 || i === convDirs.length - 1) {
      process.stdout.write(`\r  Progress: ${i + 1}/${convDirs.length} sessions (${totalMessages} messages embedded)   `);
    }
    // Offset so the Stop hook doesn't re-send already-imported lines.
    try {
      const lc = readFileSync(filePath, 'utf-8').split('\n').filter(Boolean).length;
      writeFileSync(join(OFFSETS_DIR, convId), String(lc), 'utf-8');
    } catch {}
  }

  if (totalSessions > 0) process.stdout.write('\n');
  return { sessions: totalSessions, messages: totalMessages };
}

function parseTranscriptFile(filePath: string): TranscriptMessage[] {
  const content = readFileSync(filePath, 'utf-8');
  const lines = content.split('\n').filter(Boolean);
  const messages: TranscriptMessage[] = [];

  for (let i = 0; i < lines.length; i++) {
    try {
      const entry = JSON.parse(lines[i]);
      if (entry.type !== 'user' && entry.type !== 'assistant') continue;

      const msg: TranscriptMessage = {
        message_index: i,
        type: entry.type,
        content: extractTextContent(entry.message?.content),
      };

      if (entry.type === 'assistant') {
        if (Array.isArray(entry.message?.content)) {
          const toolCalls = entry.message.content
            .filter((b: any) => b?.type === 'tool_use')
            .map((b: any) => ({
              name: b.name || '',
              input: JSON.stringify(b.input || {}).slice(0, 500),
              id: b.id || '',
            }));
          if (toolCalls.length > 0) msg.tool_calls = toolCalls;
        }
        if (entry.message?.model) msg.model = entry.message.model;
        if (entry.message?.usage) {
          msg.usage = {
            input_tokens: entry.message.usage.input_tokens,
            output_tokens: entry.message.usage.output_tokens,
            cache_creation_input_tokens: entry.message.usage.cache_creation_input_tokens,
            cache_read_input_tokens: entry.message.usage.cache_read_input_tokens,
          };
        }
      }

      if (msg.content.length > 0) messages.push(msg);
    } catch {}
  }

  return messages;
}

async function syncTranscriptsLocal(mcpPort: number, mcpToken: string, repo: string): Promise<{ sessions: number; messages: number }> {
  const projectsDir = getClaudeProjectsFolder();
  if (!projectsDir) return { sessions: 0, messages: 0 };

  const files = readdirSync(projectsDir).filter(f => f.endsWith('.jsonl'));
  if (files.length === 0) return { sessions: 0, messages: 0 };

  console.log(`  Found ${files.length} CC session transcripts, importing + embedding...`);

  let totalSessions = 0;
  let totalMessages = 0;

  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    const sessionId = file.replace('.jsonl', '');
    const filePath = join(projectsDir, file);

    try {
      const allMessages = parseTranscriptFile(filePath);
      const messages = allMessages.length > 500 ? allMessages.slice(-500) : allMessages;
      if (messages.length === 0) continue;

      const resp = await fetch(`http://127.0.0.1:${mcpPort}/api/conversation-sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${mcpToken}` },
        body: JSON.stringify({ session_id: sessionId, repo, messages }),
        signal: AbortSignal.timeout(15000),
      });
      if (resp.ok) {
        const result = await resp.json() as { ingested?: number };
        totalSessions++;
        totalMessages += result.ingested ?? messages.length;
      }
    } catch {}

    if ((i + 1) % 10 === 0 || i === files.length - 1) {
      process.stdout.write(`\r  Progress: ${i + 1}/${files.length} sessions (${totalMessages} messages embedded)   `);
    }

    // Write offset so the Stop hook doesn't re-send
    try {
      const content = readFileSync(join(projectsDir, file), 'utf-8');
      const lineCount = content.split('\n').filter(Boolean).length;
      writeFileSync(join(OFFSETS_DIR, sessionId), String(lineCount), 'utf-8');
    } catch {}
  }

  if (totalSessions > 0) process.stdout.write('\n');
  return { sessions: totalSessions, messages: totalMessages };
}

async function syncTranscriptsBulk(gatewayUrl: string, token: string, repo: string): Promise<{ sessions: number; messages: number }> {
  const projectsDir = getClaudeProjectsFolder();
  if (!projectsDir) return { sessions: 0, messages: 0 };

  const files = readdirSync(projectsDir).filter(f => f.endsWith('.jsonl'));
  if (files.length === 0) return { sessions: 0, messages: 0 };

  console.log(`Found ${files.length} CC session transcripts, syncing...`);

  const maxMessagesPerSession = 500;
  let totalSessions = 0;
  let totalMessages = 0;

  // Batch sessions into groups of 5
  for (let i = 0; i < files.length; i += 5) {
    const batch = files.slice(i, i + 5);
    const sessions: Array<{ cc_session_id: string; messages: TranscriptMessage[] }> = [];

    for (const file of batch) {
      const sessionId = file.replace('.jsonl', '');
      const filePath = join(projectsDir, file);

      try {
        const allMessages = parseTranscriptFile(filePath);
        const messages = allMessages.length > maxMessagesPerSession
          ? allMessages.slice(-maxMessagesPerSession)
          : allMessages;

        if (messages.length > 0) {
          sessions.push({ cc_session_id: sessionId, messages });
        }
      } catch {}
    }

    if (sessions.length === 0) continue;

    try {
      const resp = await fetch(`${gatewayUrl}/api/v1/cli/sync-transcripts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ repo, sessions }),
      });
      if (resp.ok) {
        const result = await resp.json() as { accepted: number; sessions: number };
        totalMessages += result.accepted;
        totalSessions += result.sessions;
      }
    } catch {}

    // Write offset files so the Stop hook doesn't re-send
    for (const file of batch) {
      const sessionId = file.replace('.jsonl', '');
      const filePath = join(projectsDir, file);
      try {
        const content = readFileSync(filePath, 'utf-8');
        const lineCount = content.split('\n').filter(Boolean).length;
        writeFileSync(join(OFFSETS_DIR, sessionId), String(lineCount), 'utf-8');
      } catch {}
    }
  }

  return { sessions: totalSessions, messages: totalMessages };
}
